// Ludo page — ported pixel-for-pixel from ludo_client.php.
// The entire Ludo experience (lobby, matchmaking, VS intro, game board,
// result/recovery, quit-confirm) lives inside a fixed-position .ludo-root
// wrapper so it overlays the site's NavBar/BottomNav chrome.
//
// CSS lives in ./ludo.css (verbatim from the PHP <style> block).

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '@baaligames/shared-ui/lib/api';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import { setWalletBalance, refreshWallet } from '@baaligames/shared-ui/lib/wallet';
import GameSettingsMenu from '@baaligames/shared-ui/components/GameSettingsMenu';
import LobbyBanner from '@baaligames/shared-ui/components/LobbyBanner';
import GameLoadingScreen from '@baaligames/shared-ui/components/GameLoadingScreen';
import { useAssetPreloader, flattenImageList } from '@baaligames/shared-ui/lib/useAssetPreloader';
import ConcurrentMatchModal from '@baaligames/shared-ui/components/ConcurrentMatchModal';
import { isSessionAcked, markSessionAcked, clearSessionAcked } from '@baaligames/shared-ui/lib/recoveryAck';
import { LudoWS } from './ws';
import { WSVoice } from './wsVoice';
import type { WSStatus } from './ws';
import {
  Color,
  COLOR_CONFIG,
  PANEL_ORDER,
  YARD,
  SAFE_ZONES,
  TRACK_COORDS,
  HOME_COORDS,
} from './board';
import Board, { BoardToken, BoardFx, TokenDeco } from './Board';
import {
  primeAudio,
  installAudioUnlock,
  setSoundEnabled as setSfxEnabled,
  playDiceRollSound,
  playJumpSound,
  playCaptureSound,
  playHomeSound,
  playSafeZoneSound,
  startShameTone,
  dropShameTone,
  stopShameTone,
  startUrgencyBeep,
  stopUrgencyBeep,
  playKickSound,
  playWinFanfare,
  playLossTone,
  playCancelSound,
  playTap,
  playSlotLock,
  playYourTurnChime,
} from './sounds';
import './ludo.css';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import Icon from '@baaligames/shared-ui/components/Icon';
import InsufficientFundsModal from '@baaligames/shared-ui/components/InsufficientFundsModal';

type Config = {
  audioEnabled: boolean;
  platformFeePercent: number;
  betTiers: number[];
  playerModes: number[];
  stunServers: string[];
  // Server-driven UX knobs added in Phase 3/5 — admin edits propagate
  // without redeploy. All optional so older API responses still satisfy
  // the type during the rollout.
  reconnectWindowSeconds?: number;
  abandonAction?: 'refund' | 'forfeit';
  urgencyBeepLeadSeconds?: number;
  lobbyBanner?: { enabled: boolean; text: string; tone: 'info' | 'warning' | 'critical' };
  // TURN relays (optional). Server always sends the key, even as empty
  // array, so the shape is guaranteed. Each entry has { urls, username,
  // credential } shape matching RTCIceServer. Needed for NAT traversal
  // when STUN alone can't connect two peers (common on mobile data).
  turnServers?: RTCIceServer[];
};

type Profile = { displayName: string; name?: string; avatarUrl?: string | null; isBot: boolean; wallet?: number };
type ResultKind = {
  result: 'win' | 'loss' | 'kick';
  payout: number;
  pot: number;
  sessionId: number;
  // [I] Winner's colour — drives the "<COLOR> TAKES THE CROWN" / "<COLOR>
  // WON THE POT" subtitle on the result screen, matching PHP
  // showResultScreen(kind, { winnerColor }).
  winnerColor?: string;
  // [D] Present on recovery fetches and local game-over; kept as-is for
  // both paths so RecoveryScreen can render PHP-parity labels + fee line.
  betAmount?: number;
  platformFee?: number;
  opponents?: Array<{ name?: string; color?: string; is_bot?: boolean }>;
};
type Phase = 'lobby' | 'queue' | 'vs' | 'playing' | 'result';

// Images — must match filenames under /public/games/ludo/.
const IMG = {
  settings: '/games/ludo/Settings.png',
  coin: '/games/ludo/Coin.png',
  plus: '/games/ludo/Plus.png',
  logo: '/games/ludo/Logo.png',
  pawns2: '/games/ludo/Pawn-2.png',
  pawns4: '/games/ludo/Pawn-4.png',
  cash1: '/games/ludo/Cash-1.png',
  cash2: '/games/ludo/Cash-2.png',
  cash3: '/games/ludo/Cash-3.png',
  cash4: '/games/ludo/Cash-4.png',
  victory: '/games/ludo/Victory_Trophy.png',
  defeat: '/games/ludo/Defeat_Medal.png',
  kicked: '/games/ludo/Kicked_Boot.png',
  warn: '/games/ludo/Quit_Warning.png',
} as const;

// Pair bet-tier → stack image. Matches PHP ordering.
const CASH_IMAGES = [IMG.cash1, IMG.cash2, IMG.cash3, IMG.cash4];

function fmt(n: number) { return Math.floor(n).toLocaleString('en-IN'); }

// Stable list of every URL the Ludo lobby + match screen depends on.
// Declared at module scope so its identity is stable across renders
// — useAssetPreloader re-runs only when this array's identity changes,
// so a moving identity would restart the preload on every render.
const LUDO_PRELOAD_URLS = flattenImageList(IMG);

export default function Ludo() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const toast = useToast();
  // Asset preloader — keeps the loading screen visible until every
  // image referenced in IMG has settled (loaded or errored). Combined
  // below with the WS-auth gate so the lobby only renders after BOTH
  // signals are green.
  const assetStatus = useAssetPreloader(LUDO_PRELOAD_URLS, 600);

  // ─── Server config ───────────────────────────────────────────────
  const [config, setConfig] = useState<Config | null>(null);
  const [wallet, setWallet] = useState<number>(10_000);
  const [online, setOnline] = useState<number>(0);
  // Concurrent-match modal state — see chess/Page.tsx for the rationale.
  const [concBlocking, setConcBlocking] = useState<any[] | null>(null);
  const [concOpen,     setConcOpen]     = useState(false);
  // Per-stake queue badge state.
  //   `waitingByBet[bet]`         — real humans queued at that stake
  //                                  summed across player-counts.
  //                                  Kept for any future consumer that
  //                                  wants the cross-pc rollup.
  //   `waitingByBetAndPc[bet][pc]` — real humans queued at that exact
  //                                  (bet, playerCount). The lobby
  //                                  badge MUST use this so it only
  //                                  lights up cards where clicking
  //                                  ACTUALLY joins the waiting queue
  //                                  (not a new pc-mismatched queue).
  // Pre-fix the badge used the flat `waiting_by_bet` only — a 4P
  // queued opponent lit up the same card the 2P viewer saw, and
  // clicking that card spawned a separate 2P queue. From the user's
  // perspective the badge was "on the wrong card" because it pointed
  // at a card that wouldn't actually let them join. `showWaitingBadge`
  // is the admin master toggle; when false, badges never render even
  // if the server is still sending counts.
  const [waitingByBet,      setWaitingByBet]      = useState<Record<string, number>>({});
  const [waitingByBetAndPc, setWaitingByBetAndPc] = useState<Record<string, Record<string, number>>>({});
  const [showWaitingBadge,  setShowWaitingBadge]  = useState<boolean>(false);
  const [authReady, setAuthReady] = useState(false);
  // [K] Socket status drives the top-of-screen status-box text. Matches
  // PHP #status-box transitions: connecting → open (hidden) → lost →
  // reconnecting → open.
  const [wsStatus, setWsStatus] = useState<WSStatus>('connecting');

  // ─── Lobby state ─────────────────────────────────────────────────
  const [selectedPlayers, setSelectedPlayers] = useState<2 | 4>(2);
  const [selectedBet, setSelectedBet] = useState<number | null>(null);

  // ─── Match state ─────────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>('lobby');
  // Queue state includes the profiles of users who have joined (sent by
  // the backend in every `waiting_in_queue` update). Used by the
  // WaitingModal to render real avatars for self + any already-joined
  // humans instead of the placeholder colour avatars.
  const [queue, setQueue] = useState<{
    current: number;
    required: number;
    profiles: Array<{ userId: string; name: string; avatarUrl: string | null; is_bot: boolean }>;
    yourUserId: string | null;
  } | null>(null);
  const [yourColor, setYourColor] = useState<Color | null>(null);
  const [colors, setColors] = useState<Color[]>([]);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const [tokens, setTokens] = useState<Record<string, number[]>>({});
  const [strikes, setStrikes] = useState<Record<string, number>>({});
  // Seats whose WebSocket has dropped mid-match. The seat isn't forfeited
  // yet (see LudoGame.DISCONNECT_GRACE_SECONDS on the server); we just
  // grey it out, drop its voice peer, and stop waiting for its turn.
  const [absent, setAbsent] = useState<Set<string>>(new Set());
  // Seats that have been permanently kicked (manual quit or strike-out).
  // This is terminal — unlike `absent`, kicked seats never come back.
  // The panel renders a greyed "Left" overlay and the board stops
  // rendering their tokens, matching PHP's remove-from-match visual.
  const [kicked, setKicked] = useState<Set<string>>(new Set());
  const [currentTurn, setCurrentTurn] = useState<Color | null>(null);
  const [dice, setDice] = useState<{ value: number | null; valid: number[]; player: Color | null }>(
    { value: null, valid: [], player: null }
  );
  const [pot, setPot] = useState<number>(0);
  const [result, setResult] = useState<ResultKind | null>(null);
  const [recovery, setRecovery] = useState<ResultKind | null>(null);
  const [quitConfirm, setQuitConfirm] = useState(false);
  const [vsIntroActive, setVsIntroActive] = useState(false);

  // ─── UI chrome ───────────────────────────────────────────────────
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [micMuted, setMicMuted] = useState(false);
  const [listenMuted, setListenMuted] = useState(false);
  const [autoRoll, setAutoRoll] = useState(false);
  // Incremented on every turn change — used as a React key to remount the
  // timer bar so its CSS transition restarts from 100% width.
  const [turnSeq, setTurnSeq] = useState(0);
  // When the third 6 in a row is rolled, the server sends
  // `dice_rolled` with `value: 6`, `valid_tokens: []` and
  // `message: 'Triple 6! Turn forfeit.'`. We show a ❌ overlay on the
  // dice face of the forfeiting player for ~1500ms + play a cancel
  // buzzer so the forfeit is visible and audible (without this the
  // turn just silently passes).
  const [tripleSixCancel, setTripleSixCancel] = useState<Color | null>(null);
  // Insufficient-funds modal state — shown when a bet is tapped but
  // the wallet can't cover it, OR when the server rejects join_queue
  // with `insufficient_funds`. `insufficientBet` remembers the exact
  // amount the user tried so the modal's "you needX" line is right.
  const [insufficientOpen, setInsufficientOpen] = useState(false);
  const [insufficientBet, setInsufficientBet] = useState<number>(0);
  const [boardFx, setBoardFx] = useState<BoardFx[]>([]);
  const [tokenDeco, setTokenDeco] = useState<TokenDeco>({});
  const [botActivateAt, setBotActivateAt] = useState<number | null>(null);
  const fxIdRef = useRef(1);

  // ─── Refs for stable WS handlers ─────────────────────────────────
  const wsRef = useRef<LudoWS | null>(null);
  const voiceRef = useRef<WSVoice | null>(null);
  const yourColorRef = useRef<Color | null>(null);
  const phaseRef = useRef<Phase>('lobby');
  const potRef = useRef<number>(0);
  // Serialised WS message queue — mirrors PHP client's `messageQueue` +
  // `isAnimating` pattern so animations/sounds always play through before the
  // next state change. Most importantly, this holds the rolled dice value on
  // screen long enough for the user to see it when the server has no valid
  // moves and immediately follows up with `turn_update`.
  const msgQueueRef = useRef<any[]>([]);
  const processingRef = useRef<boolean>(false);
  // Mirror of `tokens` for async-queue reads — the ordered-message
  // handler runs inside a closure created at mount, so direct reads of
  // the `tokens` state would see the initial {} forever. Keep a ref in
  // sync so step-walk animation can compute `new_pos - startPos`.
  const tokensRef = useRef<Record<string, number[]>>({});
  useEffect(() => { yourColorRef.current = yourColor; }, [yourColor]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // Auth safety net. If the user session disappears while the Ludo
  // page is mounted (token expiry, logout from another tab, manual
  // cookie clear), tear down the WebSocket + voice and bounce to
  // /login. Without this, the already-authenticated socket keeps the
  // game playable server-side even though HTTP calls would 401. The
  // top-level `<Protected>` wrapper normally handles this, but it
  // re-evaluates on re-render — closing the live socket here makes
  // sure the game stops accepting input the instant auth is gone.
  useEffect(() => {
    if (authLoading) return;
    if (user === null) {
      try { wsRef.current?.close(); } catch {}
      try { voiceRef.current?.stop(); } catch {}
      wsRef.current = null;
      voiceRef.current = null;
      nav('/login', { replace: true });
    }
  }, [authLoading, user, nav]);
  useEffect(() => { potRef.current = pot; }, [pot]);
  useEffect(() => { tokensRef.current = tokens; }, [tokens]);

  // turnSeq is bumped EXPLICITLY from the turn_update and dice_rolled
  // handlers (see handleOrdered). We do NOT bump it here via a useEffect
  // on currentTurn because that only fires on color change — a same-color
  // bonus turn or a dice roll restarts the server's 18s timer but leaves
  // currentTurn unchanged, and the client must still restart its visual
  // bar + urgency tone. PHP parity: startClientTimer is called on every
  // `turn_update` AND on `dice_rolled` with valid tokens.

  // Reset auto-roll whenever we leave an active game.
  useEffect(() => {
    if (phase !== 'playing') setAutoRoll(false);
  }, [phase]);

  // Screen wake lock — keeps the device from dimming / auto-locking
  // while the user is in an active game flow (queue / VS / playing).
  // Released in the lobby and result screens so we don't pin the
  // screen on when the player is just browsing.
  //
  // Uses the Screen Wake Lock API (Chrome/Edge/Safari 16.4+/Firefox
  // with flag). Silently no-ops on older browsers. Re-acquires on
  // visibilitychange because the OS releases the lock whenever the
  // tab goes to the background — coming back to a visible tab
  // without this re-request would leave the screen unlocked.
  useEffect(() => {
    const active = phase === 'queue' || phase === 'vs' || phase === 'playing';
    if (!active) return;
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;

    let wakeLock: any = null;
    let cancelled = false;

    const request = async () => {
      try {
        const w = await (navigator as any).wakeLock.request('screen');
        if (cancelled) { try { w.release(); } catch {} return; }
        wakeLock = w;
        w.addEventListener?.('release', () => { wakeLock = null; });
      } catch {
        // User-agent may refuse (battery saver, permission, etc.) — no-op.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !wakeLock && !cancelled) {
        void request();
      }
    };

    void request();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (wakeLock) { try { wakeLock.release(); } catch {} wakeLock = null; }
    };
  }, [phase]);

  // Keep the WebAudio module in sync with the UI sound toggle.
  useEffect(() => { setSfxEnabled(soundEnabled); }, [soundEnabled]);

  // Your-turn chime — a soft two-note rise when the turn transitions
  // TO the local player. Skipped during the VS intro (the match-start
  // flow has its own audio) and when the first turn of a game begins
  // on us (feels redundant right after the fanfare-adjacent transition).
  const prevTurnForChimeRef = useRef<Color | null>(null);
  useEffect(() => {
    const prev = prevTurnForChimeRef.current;
    prevTurnForChimeRef.current = currentTurn;
    if (phase !== 'playing') return;
    if (!yourColor) return;
    if (currentTurn !== yourColor) return;
    if (prev === yourColor) return; // already our turn (bonus roll) — skip
    playYourTurnChime();
  }, [currentTurn, yourColor, phase]);

  // Slot-lock bloop + pop. When the waiting-queue's locked count goes
  // up, play the rising three-note bloop. Keeps the queue feeling
  // alive as slots fill instead of silent ticks.
  const prevCurrentRef = useRef(0);
  useEffect(() => {
    const prev = prevCurrentRef.current;
    const next = queue?.current ?? 0;
    prevCurrentRef.current = next;
    if (phase !== 'queue') return;
    if (next > prev && next > 0) playSlotLock();
  }, [queue?.current, phase]);

  // Urgency beep — matches PHP startClientTimer, plus an explicit
  // stop-at-bar-end so the beep can never outlive the visible timer
  // bar. See PROJECT_NOTES.md §2026-04-22 fix for why.
  //
  //   • Plays for EVERY user (active player + observers). PHP parity.
  //   • Client bar is 16s, urgency fires t=12s → 4s of beeping.
  //   • Auto-stops at t=16s (= bar's visual end). Without this second
  //     timer the beep kept ticking every 600ms until the server's
  //     slower 18s turn-timeout fired an action, which sounded like
  //     "beeps continue after the bar is empty".
  //   • Restarts whenever turnSeq bumps (new turn / bonus turn /
  //     dice_rolled with valid tokens).
  //   • Stopped on phase change away from 'playing'.
  useEffect(() => {
    stopUrgencyBeep();
    if (phase !== 'playing') return;
    if (!currentTurn) return;
    // Bar duration is 16 s (matches the visible TimerFill animation).
    // Urgency-beep lead is admin-tunable via URGENCY_BEEP_LEAD_SECONDS;
    // 0 disables the beep entirely.
    const BAR_END_MS = 16_000;
    const leadSec    = Number(config?.urgencyBeepLeadSeconds ?? 4);
    if (leadSec <= 0) return;
    const URGENCY_START_MS = Math.max(0, BAR_END_MS - leadSec * 1000);
    const startT = setTimeout(() => startUrgencyBeep(), URGENCY_START_MS);
    const stopT  = setTimeout(() => stopUrgencyBeep(), BAR_END_MS);
    return () => {
      clearTimeout(startT);
      clearTimeout(stopT);
      stopUrgencyBeep();
    };
  }, [phase, currentTurn, turnSeq, config?.urgencyBeepLeadSeconds]);

  // ─── Hide site chrome while Ludo is mounted ──────────────────────
  useLayoutEffect(() => {
    document.body.classList.add('ludo-active');
    return () => document.body.classList.remove('ludo-active');
  }, []);

  // Install a document-level first-click audio unlock as soon as the
  // page mounts. The FIRST real interaction anywhere on the page
  // (cash-tile tap, settings button, dice click, menu open) will
  // resume the AudioContext — so even if the user reaches the game
  // via reconnect or a weird route, sound works.
  useEffect(() => { installAudioUnlock(); }, []);

  // Pull the real wallet balance on mount so the lobby header shows it
  // immediately (backend source of truth = GET /api/ludo/wallet, which
  // internally ensures a row exists for every authenticated user).
  useEffect(() => { void refreshWallet(); }, []);

  // ─── WebSocket + config bootstrap ────────────────────────────────
  useEffect(() => {
    let closed = false;
    (async () => {
      try {
        const cfg = await api.get<Config>('/ludo/config');
        if (closed) return;
        setConfig(cfg);
      } catch {}
      try {
        const w = await api.get<{ balance: number }>('/ludo/wallet');
        if (!closed) setWallet(w.balance);
      } catch {}
      // Fetch any unacknowledged previous-game result, but DELAY
      // committing it to state by ~1.2s. If the WS reconnect_sync
      // arrives during that window (meaning we're actually being
      // dropped back into an active match), `setRecovery(null)` in
      // that handler wins and the stale popup never shows. This
      // stops the "I got kicked out!" confusion when a mid-game
      // refresh races the HTTP endpoint ahead of the WS resync.
      try {
        const pr = await api.get<{ pending: any }>('/ludo/pending-result');
        if (!closed && pr?.pending) {
          // Skip & retry-ack: if this session was already dismissed
          // locally on a previous visit but the server still thinks
          // it's pending, fire a background acknowledge and DO NOT
          // show the popup again.
          if (isSessionAcked('ludo', pr.pending.sessionId)) {
            api.post('/ludo/acknowledge-result', { sessionId: pr.pending.sessionId })
              .then(() => clearSessionAcked('ludo', pr.pending.sessionId))
              .catch(() => {});
          } else {
            setTimeout(() => {
              // `phaseRef` captures the latest phase via ref; if the WS
              // has moved us into an active match by now, skip the popup.
              if (closed) return;
              if (phaseRef.current !== 'lobby') return;
              setRecovery({
                result: pr.pending.result,
                payout: pr.pending.payout,
                pot: pr.pending.pot,
                sessionId: pr.pending.sessionId,
                betAmount: pr.pending.betAmount,
                platformFee: pr.pending.platformFee,
                opponents: pr.pending.opponents,
              });
            }, 1200);
          }
        }
      } catch {}
      const ws = new LudoWS();
      ws.on(onMessage);
      ws.onStatus(setWsStatus);
      ws.connect();
      wsRef.current = ws;
    })();
    return () => {
      closed = true;
      wsRef.current?.close();
      voiceRef.current?.stop();
      wsRef.current = null;
      voiceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default bet once config arrives.
  useEffect(() => {
    if (config && selectedBet === null) {
      const candidates = config.betTiers.filter((b) => b > 0);
      setSelectedBet(candidates[0] ?? null);
    }
  }, [config, selectedBet]);

  // ─── WS message handling (queued so animations play through) ─────
  // Fast (unordered) messages are applied immediately; ordered
  // gameplay events go through the async queue so the prior animation
  // always finishes first (mirrors PHP client `isAnimating`).
  const ORDERED_TYPES = new Set([
    'dice_rolled',
    'turn_update',
    'token_moved',
    'token_killed',
    'game_over',
    'player_kicked',
  ]);

  function onMessage(msg: any) {
    if (ORDERED_TYPES.has(msg.type)) {
      // EAGER visual cleanup for a non-self kick. Without this, the
      // `player_kicked` frame queues behind up-to-6 strike-timeout
      // auto-play animations (dice_rolled + token_moved per strike =
      // ~3 s each), and the kicked player's tokens keep animating on
      // the remaining players' boards until the queue finally drains.
      // Running the visual side-effects (drop voice peer, mark seat
      // kicked so `boardTokens` filters it and `PlayerPanel` greys it)
      // the moment the message arrives gives instant feedback. The
      // full handler (kick sound, self-branch result-screen transition)
      // still fires in order once the queue catches up.
      if (msg.type === 'player_kicked' && msg.player !== yourColorRef.current) {
        try { voiceRef.current?.dropPeer?.(msg.player); } catch {}
        setTokens((t) => {
          if (!t[msg.player]) return t;
          const next = { ...t };
          delete next[msg.player];
          return next;
        });
        setKicked((prev) => {
          if (prev.has(msg.player)) return prev;
          const next = new Set(prev);
          next.add(msg.player);
          return next;
        });
        setAbsent((prev) => {
          if (!prev.has(msg.player)) return prev;
          const next = new Set(prev);
          next.delete(msg.player);
          return next;
        });
      }
      msgQueueRef.current.push(msg);
      void processQueue();
      return;
    }
    applyImmediate(msg);
  }

  // Non-gameplay / metadata messages — safe to apply instantly.
  function applyImmediate(msg: any) {
    switch (msg.type) {
      case 'auth_success': setAuthReady(true); break;
      case 'you_are': setYourColor(msg.color); yourColorRef.current = msg.color; break;
      case 'global_stats':
        setOnline(msg.online_players || 0);
        // Admin-controlled flag lives on the same payload so a toggle
        // in the admin panel propagates within one broadcast cycle.
        if (typeof msg.show_waiting_badge === 'boolean') {
          setShowWaitingBadge(msg.show_waiting_badge);
        }
        if (msg.waiting_by_bet && typeof msg.waiting_by_bet === 'object') {
          setWaitingByBet(msg.waiting_by_bet);
        }
        // Authoritative per-(bet, playerCount) map. Older server
        // builds may not send this field; the badge logic falls back
        // to {} which renders no badges (safe default).
        if (msg.waiting_by_bet_and_pc && typeof msg.waiting_by_bet_and_pc === 'object') {
          setWaitingByBetAndPc(msg.waiting_by_bet_and_pc);
        }
        break;
      case 'wallet_update':
        // Live balance push from the backend (bet debit, win credit).
        // Updates the NavBar chip + Wallet page through the shared store.
        if (typeof msg.balance === 'number') setWalletBalance(msg.balance);
        break;
      case 'error':
        // Server rejected the action. The only case we surface a
        // dedicated UX for is `insufficient_funds` — pop the recharge
        // modal and roll the phase back to the lobby so the user
        // isn't stranded on a fake "queue" screen.
        if (msg.code === 'insufficient_funds') {
          setInsufficientBet(selectedBet || 0);
          setInsufficientOpen(true);
          setPhase('lobby');
          setQueue(null);
        } else if (msg.code === 'concurrent_match_limit') {
          setConcBlocking(Array.isArray(msg.blocking) ? msg.blocking : []);
          setConcOpen(true);
          setPhase('lobby');
          setQueue(null);
        } else if (msg.code === 'daily_loss_cap') {
          toast.push('error', 'You\'ve reached your daily loss limit. Try again tomorrow.');
          setPhase('lobby');
          setQueue(null);
        } else if (msg.code === 'bet_limit') {
          toast.push('error', msg.message || 'This bet is above your limit — contact your agent.');
          setPhase('lobby');
          setQueue(null);
        }
        break;
      case 'waiting_in_queue':
        setPhase('queue');
        setQueue({
          current: msg.current_players,
          required: msg.required_players,
          profiles: Array.isArray(msg.profiles) ? msg.profiles : [],
          yourUserId: msg.your_user_id || null,
        });
        setBotActivateAt(typeof msg.bot_activate_at === 'number' ? msg.bot_activate_at : null);
        break;
      case 'six_rolled':
      case 'bonus_turn_earned':
        // Intentionally silent. Previously these fired toasts ("6!",
        // "CAPTURE — BONUS!") which (a) were inconsistent — only 6s got
        // toasted, not 1–5; and (b) raced ahead of the dice reveal
        // animation and spoiled the outcome. Toasts are now reserved
        // for system / settings events only (refunds, mic errors,
        // voice-chat status). Dice face, strike dots, and the
        // current-turn indicator are enough to communicate the game
        // state without annotation.
        break;
      case 'refund_issued':
        // Server refunded our stake after a session aborted with no winner.
        if (typeof msg.balance === 'number') setWalletBalance(msg.balance);
        toast.push('info', `Refunded ${msg.amount}`);
        break;
      case 'game_start': {
        setColors(msg.players);
        setProfiles(msg.playerProfiles || {});
        const fresh: Record<string, number[]> = {};
        const freshStrikes: Record<string, number> = {};
        for (const c of msg.players) { fresh[c] = [YARD, YARD, YARD, YARD]; freshStrikes[c] = 0; }
        setTokens(fresh);
        setStrikes(freshStrikes);
        // A brand-new match — nobody is kicked or absent yet. Reset
        // both sets so leftover state from a previous game doesn't
        // grey out a seat before the first move.
        setKicked(new Set());
        setAbsent(new Set());
        setBoardFx([]);
        setTokenDeco({});
        setBotActivateAt(null);
        setCurrentTurn(msg.turn);
        setPot(msg.pot);
        setDice({ value: null, valid: [], player: null });
        setResult(null);
        setVsIntroActive(true);
        setPhase('vs');
        // Pass playerProfiles directly — React state hasn't flushed yet,
        // so reading `profiles` inside startVoice would get the empty map.
        startVoice(msg.players, yourColorRef.current, msg.playerProfiles || {});
        // Hold VS intro 3s (matches PHP playVSScreen).
        setTimeout(() => { setVsIntroActive(false); setPhase('playing'); }, 3000);
        break;
      }
      case 'reconnect_sync': {
        // [L] Full-state restore after the socket came back. Mirrors PHP
        // handleReconnectionSync: swap to the game screen, re-seat the
        // panels via yourColor, rehydrate tokens / strikes / turn / pot,
        // clear any stale dice/result leftovers from the previous mount,
        // and re-arm voice (peer connections went down with the old ws).
        setColors(msg.players);
        setProfiles(msg.playerProfiles || {});
        const t: Record<string, number[]> = {};
        for (const c of msg.players as Color[]) t[c] = [YARD, YARD, YARD, YARD];
        // Backend sends `tokens` keyed by colour: { blue:[...], red:[...] }.
        // Accept that shape first, then fall back to the PHP array-of-objects
        // shape so reconnects survive an older server too.
        if (msg.tokens && !Array.isArray(msg.tokens)) {
          for (const c of Object.keys(msg.tokens)) {
            const arr = msg.tokens[c];
            if (Array.isArray(arr)) t[c as Color] = arr.slice(0, 4) as number[];
          }
        } else {
          for (const tk of msg.tokens || []) {
            if (!t[tk.color]) t[tk.color] = [YARD, YARD, YARD, YARD];
            t[tk.color][tk.token_index] = tk.pos;
          }
        }
        setTokens(t);
        setStrikes(msg.strikes || {});
        // Rehydrate the kicked set from the server snapshot so a
        // reconnect during a match where someone already quit doesn't
        // paint those forfeited seats back onto the board.
        setKicked(new Set(Array.isArray(msg.kicked) ? msg.kicked : []));
        setAbsent(new Set());
        setCurrentTurn(msg.turn);
        setPot(msg.pot);
        setYourColor(msg.your_color);
        yourColorRef.current = msg.your_color;
        // Clear transient state that belonged to the previous socket.
        setDice({ value: null, valid: [], player: null });
        setResult(null);
        setQueue(null);
        stopUrgencyBeep();
        stopShameTone();
        // Drain any animation queue that was mid-flight when we lost the ws.
        flushQueue();
        setBoardFx([]);
        setTokenDeco({});
        setPhase('playing');
        // Suppress the stale "previous game" popup if we're being
        // dropped back into an in-progress match. Without this, the
        // recovery screen from /pending-result could flash for a
        // moment between page-load and the WS reconnect_sync arriving,
        // confusing a user whose connection only dropped briefly.
        setRecovery(null);
        startVoice(msg.players, msg.your_color, msg.playerProfiles || {});
        break;
      }
      case 'turn_timeout':
        setStrikes((s) => ({ ...s, [msg.player]: msg.strikes }));
        break;
      case 'left_game_success':
        setPhase('lobby');
        setQueue(null);
        break;
      case 'rtc_signal':
        voiceRef.current?.onSignal(msg.from, msg.payload);
        break;
      case 'voice_chunk':
        // New audio transport — PCM frame relayed via the server.
        voiceRef.current?.onRemoteChunk(msg.from, msg.pcm);
        break;
    }
  }

  function sleep(ms: number) {
    return new Promise<void>((r) => setTimeout(r, ms));
  }

  async function processQueue() {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (msgQueueRef.current.length) {
        const msg = msgQueueRef.current.shift()!;
        await handleOrdered(msg);
      }
    } finally {
      processingRef.current = false;
    }
  }

  // Drain the queue immediately without animations — used on quit/game_over
  // so stale gameplay messages don't keep firing after we've left.
  function flushQueue() {
    msgQueueRef.current = [];
  }

  async function handleOrdered(msg: any) {
    // Belt-and-suspenders: after a user hits Leave (phase flips to
    // `lobby`) or the match ends (phase flips to `result`), ANY game-
    // state messages still queued or arriving in-flight from the
    // server must not drive sounds, animations, or state mutations.
    // The server's broadcast layer stops sending to a kicked seat,
    // but it can't un-send frames that were already on the wire when
    // Leave was clicked. This guard makes sure those orphan frames
    // silently drop on the quitter's client instead of keeping the
    // old match audibly "alive" in the lobby. The only exception is
    // `player_kicked` for the local seat — we need that message to
    // navigate the UI into the result screen.
    const ph = phaseRef.current;
    if (ph !== 'playing' && ph !== 'vs') {
      const isSelfKick =
        msg.type === 'player_kicked' && msg.player === yourColorRef.current;
      if (!isSelfKick) return;
    }
    switch (msg.type) {
      case 'dice_rolled': {
        // Match PHP handleDiceRolled exactly: kick off the roll sound,
        // let the tumble animation play for 400ms, reveal the face,
        // THEN hold the face on-screen unconditionally for 1000ms so
        // both human and bot rolls stay readable before the next
        // action (auto-move, highlight, or turn_update) takes over.
        playDiceRollSound();
        // Tumble phase — value hidden; DiceButton animates .tumble.
        setDice({ value: null, valid: [], player: msg.player });
        await sleep(400);
        // Reveal phase — face-N appears, and the highlight of any
        // valid tokens shows up via `dice.valid`.
        const validTokens: number[] = msg.valid_tokens || [];
        setDice({ value: msg.value, valid: validTokens, player: msg.player });
        // PHP startClientTimer on dice_rolled with valid tokens: the
        // server has restarted its 18s turn timer for the move phase,
        // so the client resets its 16s bar + 12s-to-urgency window too.
        // Without this bump, the bar / beep would keep running off the
        // roll-phase clock instead of the fresh move-phase clock.
        if (validTokens.length > 0) setTurnSeq((s) => s + 1);
        // Triple-6 detection — the backend tags the forfeit with a
        // specific message. We play a cancel buzzer and drop a ❌
        // overlay on the forfeiting player's dice for ~1.5s while the
        // server advances the turn. See `ludoEngine.js` L315-316.
        const isTripleSixForfeit = msg.value === 6
          && validTokens.length === 0
          && typeof msg.message === 'string'
          && /triple\s*6/i.test(msg.message);
        if (isTripleSixForfeit) {
          playCancelSound();
          setTripleSixCancel(msg.player);
          setTimeout(() => setTripleSixCancel(null), 1500);
        }
        await sleep(1000);

        // [Auto-move parity] PHP handleDiceRolled: when the dice belongs
        // to THIS player and (there's exactly one valid token || all the
        // valid tokens sit on the same tile), fire move_token after a
        // 500ms beat instead of waiting for a click. This is what kept
        // PHP sessions from stalling on trivial single-choice rolls.
        // It is NOT gated by the auto-roll checkbox — it's always on.
        const mine = yourColorRef.current;
        if (mine && msg.player === mine && validTokens.length > 0) {
          const myTokens = tokensRef.current[mine] || [];
          const validPositions = validTokens.map((i) => myTokens[i]);
          const allSamePos =
            validPositions.length > 0 &&
            validPositions.every((p) => p === validPositions[0]);
          if (validTokens.length === 1 || allSamePos) {
            await sleep(500);
            wsRef.current?.send({ type: 'move_token', token_index: validTokens[0] });
          }
        }
        break;
      }
      case 'turn_update': {
        setCurrentTurn(msg.turn);
        setDice({ value: null, valid: [], player: null });
        // Bump turnSeq unconditionally (even if msg.turn === currentTurn,
        // which happens on same-player bonus turns). This remounts the
        // TimerFill bar and retriggers the urgency useEffect so the 12s
        // countdown-to-beep starts fresh. The explicit stopUrgencyBeep
        // that used to live here is now handled by that effect's cleanup.
        setTurnSeq((s) => s + 1);
        await sleep(100);
        break;
      }
      case 'token_moved': {
        const { player, token_index, new_pos, captured_tokens, reached_home } = msg;
        const hadCapture = Array.isArray(captured_tokens) && captured_tokens.length > 0;

        // Step-by-step hop animation — matches PHP handleTokenMoved:
        // for each step advance the token one tile, play the jump
        // sound, re-render, sleep 200ms. This is what makes the token
        // visibly "walk" rather than teleport.
        const prev = tokensRef.current[player]?.[token_index];
        const startPos = typeof prev === 'number' ? prev : YARD;
        // PHP parity (handleTokenMoved L3727-3740): yard-exit animates as
        // a single hop to pos 0 (playJumpSound + 300ms). Any on-track
        // move walks tile-by-tile from startPos+1 → new_pos at 200ms each.
        const steppable = startPos >= 0 && new_pos >= 0 && new_pos > startPos;
        const yardExit = startPos === YARD && new_pos >= 0;
        if (yardExit) {
          // First hop: -1 → 0. PHP uses sleep(300) for this, not 200.
          flushSync(() => {
            setTokens((t) => {
              const next: Record<string, number[]> = { ...t };
              if (next[player]) {
                const arr = next[player].slice();
                arr[token_index] = 0;
                next[player] = arr;
              }
              return next;
            });
          });
          playJumpSound();
          await sleep(300);
          // Usually yard-exit lands on pos 0 and new_pos === 0, but if the
          // server ever combined yard-exit with a forward hop we keep walking.
          for (let p = 1; p <= new_pos; p++) {
            flushSync(() => {
              setTokens((t) => {
                const next: Record<string, number[]> = { ...t };
                if (next[player]) {
                  const arr = next[player].slice();
                  arr[token_index] = p;
                  next[player] = arr;
                }
                return next;
              });
            });
            playJumpSound();
            await sleep(200);
          }
        } else if (steppable) {
          const steps = new_pos - startPos;
          for (let i = 1; i <= steps; i++) {
            const pos = startPos + i;
            // flushSync forces React to commit this render *before*
            // returning — without it React 18 may coalesce the 5
            // step-updates inside the for-loop into a single paint,
            // which is exactly the "jumps straight to the 5th block"
            // bug the user reported. The awaited sleep(200) alone is
            // not enough: setState inside async handlers is still
            // subject to automatic batching, and concurrent mode is
            // free to defer paints across microtasks.
            flushSync(() => {
              setTokens((t) => {
                const next: Record<string, number[]> = { ...t };
                if (next[player]) {
                  const arr = next[player].slice();
                  arr[token_index] = pos;
                  next[player] = arr;
                }
                return next;
              });
            });
            playJumpSound();
            await sleep(200);
          }
        } else {
          // Anomalous jump (e.g. backwards via reconnect) — commit at once.
          setTokens((t) => {
            const next: Record<string, number[]> = { ...t };
            if (next[player]) {
              const arr = next[player].slice();
              arr[token_index] = new_pos;
              next[player] = arr;
            }
            return next;
          });
          playJumpSound();
          await sleep(200);
        }

        // One-shot landing bounce after the walk — applies to every
        // step-walked arrival and yard-exit hops. Auto-clears after
        // 350ms so the class comes off and the token settles to its
        // rest transform without lingering state.
        if (steppable || yardExit) {
          const decoKey = `${player}-${token_index}`;
          setTokenDeco((d) => ({ ...d, [decoKey]: 'just-landed' }));
          setTimeout(() => {
            setTokenDeco((d) => {
              if (d[decoKey] !== 'just-landed') return d;
              const next = { ...d };
              delete next[decoKey];
              return next;
            });
          }, 350);
        }

        // Outcome cue AFTER the walk finishes: home > capture > safe.
        if (reached_home || new_pos >= 56) {
          playHomeSound();
          // One-shot gold glow on the newly-homed token (1.2s, non-repeating
          // per CSS). Auto-clear the deco once the animation ends so a
          // subsequent re-render doesn't keep the class around.
          const decoKey = `${player}-${token_index}`;
          setTokenDeco((d) => ({ ...d, [decoKey]: 'winning' }));
          setTimeout(() => {
            setTokenDeco((d) => {
              if (d[decoKey] !== 'winning') return d;
              const next = { ...d };
              delete next[decoKey];
              return next;
            });
          }, 1300);
        } else if (hadCapture) {
          playCaptureSound();
          startShameTone();
        } else {
          // Safe-zone cue when landing on one of the global safe tiles.
          let abs = -1;
          if (new_pos >= 0 && new_pos <= 50) {
            abs = (COLOR_CONFIG[player as Color].startIndex + new_pos) % 52;
          }
          if (abs >= 0 && SAFE_ZONES.has(abs)) playSafeZoneSound();
        }

        // Apply captures: retrace each victim's path back to yard one
        // tile at a time (PHP parity — the original client walks the
        // token backwards on its own colour-relative track to sell the
        // "shamed retreat" before it vanishes into the yard). Steps at
        // ~60ms each with `dropShameTone()` ramping the shame osc
        // downward as the retreat progresses.
        if (hadCapture) {
          // Skull emoji at the killer's final cell — must appear BEFORE
          // the 800ms hold so viewers register what happened. Computed
          // from the mover's current absolute cell.
          let skullR = -1, skullC = -1;
          if (new_pos >= 0 && new_pos <= 50) {
            const abs = (COLOR_CONFIG[player as Color].startIndex + new_pos) % 52;
            [skullR, skullC] = TRACK_COORDS[abs];
          } else if (new_pos >= 51 && new_pos <= 55) {
            [skullR, skullC] = HOME_COORDS[player as Color][new_pos - 51];
          }
          if (skullR > 0 && skullC > 0) {
            const sid = fxIdRef.current++;
            setBoardFx((xs) => [...xs, { id: sid, kind: 'skull', r: skullR, c: skullC }]);
            setTimeout(() => setBoardFx((xs) => xs.filter((f) => f.id !== sid)), 3000);
          }
          // Hold for a beat so viewers register what happened (PHP 800ms).
          await sleep(800);
          const victims = (captured_tokens || []).map((cap: any) => ({
            color: cap.color as Color,
            index: cap.index as number,
            // Server now sends the victim's colour-relative pos at
            // moment-of-capture. Fall back to 0 if an older server.
            startPos: typeof cap.pos === 'number' ? cap.pos : 0,
          }));
          const maxPos = Math.max(0, ...victims.map((v) => v.startPos));
          // PHP passes `stepsBack - retreatPos` to dropShameTone so the
          // oscillator bottoms out in proportion to how far the retreat
          // has travelled — a long walk-back ramps deeper than a short one.
          for (let step = maxPos, done = 0; step > 0; step--, done++) {
            flushSync(() => {
              setTokens((t) => {
                const next: Record<string, number[]> = { ...t };
                for (const v of victims) {
                  if (v.startPos < step) continue;
                  if (!next[v.color]) continue;
                  const a = next[v.color].slice();
                  a[v.index] = step - 1;
                  next[v.color] = a;
                }
                return next;
              });
            });
            dropShameTone(done + 1);
            await sleep(60);
          }
          // Final hop into the yard.
          setTokens((t) => {
            const next: Record<string, number[]> = { ...t };
            for (const v of victims) {
              if (!next[v.color]) continue;
              const a = next[v.color].slice();
              a[v.index] = YARD;
              next[v.color] = a;
            }
            return next;
          });
          stopShameTone();
          await sleep(300);
        }

        setDice({ value: null, valid: [], player: null });
        break;
      }
      case 'game_over': {
        // [I] Mirrors PHP handleGameOver: clear timer → cleanupAudio (voice
        // teardown happens BEFORE the dramatic pause so the mic is gone
        // instantly, not after 2.5s of silence) → 2500ms suspense beat →
        // show result screen. The fanfare/loss-tone plays with the screen
        // reveal, not at the event, so it lines up with the card animation.
        const mine = yourColorRef.current;
        const won = msg.winner === mine;
        stopUrgencyBeep();
        stopShameTone();
        voiceRef.current?.stop();
        voiceRef.current = null;
        const pot = Number(msg.pot || 0) | 0;
        const payout = Number(msg.payout || 0) | 0;
        const platformFee = Math.max(0, pot - payout);
        await sleep(2500);
        if (won) playWinFanfare(); else playLossTone();
        setResult({
          result: won ? 'win' : 'loss',
          payout: won ? payout : 0,
          pot,
          sessionId: 0,
          winnerColor: msg.winner,
          platformFee: won ? platformFee : 0,
        });
        setPhase('result');
        flushQueue();
        break;
      }
      case 'token_killed':
        // Kept in the ordered set for payload-shape forward-compat, but
        // the actual skull FX + retreat is driven inside `token_moved`
        // so timing matches PHP (skull appears BEFORE the retreat, not
        // after the mover's whole animation pipeline finishes).
        break;
      case 'player_disconnected': {
        // A seat's WebSocket dropped mid-match. Drop their voice peer so
        // we don't keep holding a dead audio slot, and mark the seat as
        // absent so the player panel can render greyed-out. The server
        // will automatically escalate to a full player_kicked broadcast
        // if the grace window expires without them reconnecting — that
        // handler (below) is what clears the tokens + finalises the forfeit.
        if (msg.player !== yourColorRef.current) {
          voiceRef.current?.dropPeer?.(msg.player);
        }
        setAbsent((prev) => {
          if (prev.has(msg.player)) return prev;
          const next = new Set(prev);
          next.add(msg.player);
          return next;
        });
        break;
      }
      case 'player_reconnected': {
        // Seat came back within the grace window. Undo the absent flag
        // on the player panel. The reconnecting client's own fresh
        // WSVoice instance re-announces them to peers, so there's no
        // explicit voice-rejoin message to send here.
        setAbsent((prev) => {
          if (!prev.has(msg.player)) return prev;
          const next = new Set(prev);
          next.delete(msg.player);
          return next;
        });
        break;
      }
      case 'player_kicked': {
        // DIAG: temporary — trace every kick arrival so we can pinpoint
        // whether strike-kicks aren't reaching the client vs reaching
        // but not triggering the cleanup. Remove once the intermittent
        // "kicked player's tokens stay visible" is fully understood.
        // eslint-disable-next-line no-console
        console.log('[ludo/kick]', {
          player: msg.player, reason: msg.reason, me: yourColorRef.current,
          phase: phaseRef.current, queueDepth: msgQueueRef.current.length,
        });
        // [H] Kick SFX plays for everyone at the table — matches PHP so
        // observers feel the weight of a seat emptying, not just the
        // kicked player. PHP: `playKickSound()` runs before the self-check.
        stopUrgencyBeep();
        playKickSound();
        if (msg.player === yourColorRef.current) {
          stopShameTone();
          setResult({ result: 'kick', payout: 0, pot: potRef.current, sessionId: 0 });
          setPhase('result');
          voiceRef.current?.stop();
          voiceRef.current = null;
          flushQueue();
        } else {
          // Someone ELSE left — drop their voice peer AND mark the seat
          // kicked so the board + panel stop rendering them. Just deleting
          // their tokens entry is NOT enough: `boardTokens` falls back
          // to four yard tokens when a colour is missing, which is what
          // re-painted the kicked player's pieces on the board. The
          // `kicked` Set is the terminal signal — `boardTokens` skips
          // colours in it, and `PlayerPanel` greys the card + strips
          // the profile so the seat is visibly empty.
          voiceRef.current?.dropPeer?.(msg.player);
          setTokens((t) => {
            if (!t[msg.player]) return t;
            const next = { ...t };
            delete next[msg.player];
            return next;
          });
          setKicked((prev) => {
            if (prev.has(msg.player)) return prev;
            const next = new Set(prev);
            next.add(msg.player);
            return next;
          });
          // The seat is fully out — drop any "absent" grey-out state so
          // the panel doesn't flicker between absent and kicked styles.
          setAbsent((prev) => {
            if (!prev.has(msg.player)) return prev;
            const next = new Set(prev);
            next.delete(msg.player);
            return next;
          });
        }
        break;
      }
    }
  }

  function startVoice(
    _roomColors: Color[],
    mine: Color | null,
    _profileMap?: Record<string, { isBot?: boolean } | undefined>,
  ) {
    // Note: previously this gated on `config?.audioEnabled`, but `config`
    // is loaded async via GET /ludo/config and sometimes the game_start
    // message arrived first — leaving config undefined and silently
    // aborting voice. The new WSVoice transport needs no config at all
    // (no STUN, no TURN, no server list to pass), so we bypass the gate.
    // The server-side kill-switch (LudoGame.AUDIO_ENABLED) still applies:
    // if voice is disabled server-side, relayVoiceChunk drops every frame.
    // If color isn't assigned yet (would be a server-side bug) just bail
    // silently — no user-facing message. The game itself would be broken
    // in visible ways well before voice could ever be the thing to notice.
    if (!mine) return;
    voiceRef.current?.stop();
    voiceRef.current = null;
    // New transport: audio travels over the same WebSocket as game events.
    // No ICE, no STUN, no TURN — so it works identically on any network
    // where the game itself works. The server echoes each 20 ms PCM frame
    // to the other humans in the room.
    const v = new WSVoice(mine, (m) => { wsRef.current?.send(m); });
    if (micMuted) v.setMuted(true);
    if (listenMuted) v.setListenMuted(true);
    voiceRef.current = v;
    v.start().then((ok) => {
      if (!ok) {
        toast.push('error', 'Mic blocked — check browser permissions');
      } else {
        // Generic wording — doesn't hint at how many opponents are
        // actually human; a bot game gets the same message. Shorter
        // 1.8 s lifetime so it doesn't linger over the VS intro.
        toast.push('success', 'Voice chat enabled', 1800);
      }
    });
  }

  // ─── Actions ─────────────────────────────────────────────────────
  // handlePlay accepts optional `betOverride` / `playersOverride` so
  // the tile-tap path (which fires onSelect+startMatch in the same
  // synchronous click) doesn't depend on the React state set in the
  // SAME tick. Without these args, React batches the
  // `setSelectedBet(b)` update — `selectedBet` would still be the
  // PREVIOUS value when handlePlay reads it, and the WS would send
  // the wrong bet (the user-visible symptom: "I tap ₹500, the game
  // runs at ₹50"). Internal callers that don't change state right
  // before the call may still omit the args.
  function handlePlay(betOverride?: number, playersOverride?: number) {
    const bet = betOverride ?? selectedBet;
    const players = playersOverride ?? selectedPlayers;
    if (!bet) return;
    // Pre-flight wallet check — shows the "Not enough balance" prompt
    // IMMEDIATELY without a WS round-trip. The server still checks
    // authoritatively on join_queue (insufficient_funds error), but
    // that path is now a belt-and-braces fallback rather than the
    // primary UX. Bet=0 tiles skip the check entirely (free play).
    const cost = bet * players;
    if (bet > 0 && wallet < bet) {
      setInsufficientBet(bet);
      setInsufficientOpen(true);
      return;
    }
    void cost; // reserved for future multi-seat bets
    // First user gesture after landing on the page — unlock WebAudio now so
    // every downstream sound (dice, capture, win fanfare) works on iOS/Safari.
    primeAudio();
    // Pre-warm the microphone permission prompt inside the gesture context.
    // If we wait until `game_start` arrives (seconds later, via a network
    // round-trip), Safari and some mobile Chromes count that as "no gesture"
    // and block getUserMedia silently. Grabbing the stream here means the
    // permission prompt appears the instant the user clicks Play, and the
    // MediaStream is handed to WSVoice as soon as game_start fires.
    // No toast here — the single "Voice chat enabled" toast fires from
    // startVoice once the game begins, so the flow feels like one beat
    // instead of two competing notifications.
    if (navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        .then((stream) => { (window as any).__ludoMicStream = stream; })
        .catch((e) => { console.warn('[voice] mic permission declined', e); });
    }
    wsRef.current?.send({ type: 'join_queue', bet, players });
    setPhase('queue');
    // Optimistic placeholder until the server's first `waiting_in_queue`
    // replaces it with real profile data (including our own avatar).
    setQueue({ current: 1, required: players, profiles: [], yourUserId: null });
  }
  function handleCancelQueue() { wsRef.current?.send({ type: 'leave_game' }); }
  function handleRoll() {
    if (currentTurn !== yourColor) return;
    if (dice.value != null) return;
    primeAudio();
    stopUrgencyBeep();
    wsRef.current?.send({ type: 'roll_dice' });
  }

  // Auto-roll: when it becomes my turn AND the "Auto" checkbox is on AND no
  // dice has been rolled yet, fire the roll 700ms later — mirrors the PHP
  // client's `if (autoRollCheckbox && autoRollCheckbox.checked) …` branch.
  useEffect(() => {
    if (phase !== 'playing') return;
    if (!autoRoll) return;
    if (currentTurn !== yourColor) return;
    if (dice.value != null) return;
    const t = setTimeout(() => {
      wsRef.current?.send({ type: 'roll_dice' });
    }, 700);
    return () => clearTimeout(t);
  }, [phase, autoRoll, currentTurn, yourColor, dice.value]);
  function handlePickToken(color: Color, index: number) {
    if (color !== yourColor) return;
    primeAudio();
    wsRef.current?.send({ type: 'move_token', token_index: index });
  }
  function handleQuit() {
    wsRef.current?.send({ type: 'leave_game' });
    setQuitConfirm(false);
    setPhase('lobby');
    stopUrgencyBeep();
    stopShameTone();
    flushQueue();
    voiceRef.current?.stop();
    voiceRef.current = null;
  }
  function acknowledgeRecovery() {
    if (!recovery) return;
    const sid = recovery.sessionId;
    // Mark locally FIRST so even if the server call fails the popup
    // never re-appears on this device (the original bug was exactly
    // this — a dropped POST left the DB row un-acked forever).
    markSessionAcked('ludo', sid);
    api.post('/ludo/acknowledge-result', { sessionId: sid })
      .then(() => clearSessionAcked('ludo', sid))
      .catch(() => {/* silent — local mark alone is enough to hide */})
      .finally(() => setRecovery(null));
  }
  // Mic button doubles as the last-chance permission prompt. If the pre-warm
  // at handlePlay() was silently denied (iOS Safari during a page reload,
  // Chrome autoplay policy quirks, etc.), the VoiceMesh starts trackless and
  // no sound ever flows. Clicking the mic icon should then do what the user
  // intuitively expects: ask for the mic permission, wire the live stream
  // into every open RTCPeerConnection, and renegotiate so the remote side
  // actually hears us.
  async function toggleMic() {
    const mesh = voiceRef.current;
    // No mesh yet (still in lobby / vs screen): just flip the UI state so it
    // persists into the next game — startVoice() will pick it up.
    if (!mesh) {
      setMicMuted(!micMuted);
      return;
    }
    // If the mesh is live but has no local stream, this is the fallback path:
    // explicitly request the mic NOW (user click = valid gesture) and attach.
    if (!mesh.hasLocalStream()) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        await mesh.attachLocalStream(stream);
        setMicMuted(false);
        toast.push('success', 'Mic on', 1800);
      } catch (e: any) {
        toast.push('error', `Mic blocked: ${e?.name || e?.message || 'unknown'}`);
      }
      return;
    }
    const nextMuted = !micMuted;
    setMicMuted(nextMuted);
    mesh.setMuted(nextMuted);
  }

  // ─── Panel ordering (pos-bl always = yourColor; clockwise canonical). ─
  const panelMap = useMemo(() => {
    const mine = yourColor || 'blue';
    const idx = PANEL_ORDER.indexOf(mine);
    return {
      bl: mine,
      tl: PANEL_ORDER[(idx + 1) % 4],
      tr: PANEL_ORDER[(idx + 2) % 4],
      br: PANEL_ORDER[(idx + 3) % 4],
    };
  }, [yourColor]);

  // ─── Flat token list for the Board component. ─────────────────────
  const boardTokens: BoardToken[] = useMemo(() => {
    const out: BoardToken[] = [];
    for (const c of colors) {
      // Skip any seat that has been kicked — their tokens are out of
      // the game for good. Without this guard, the `|| [YARD, YARD, …]`
      // fallback below would repaint four fresh yard tokens for the
      // kicked colour on the board (the symptom "quitter's tokens
      // stayed visible on my screen").
      if (kicked.has(c)) continue;
      const arr = tokens[c] || [YARD, YARD, YARD, YARD];
      arr.forEach((pos, idx) => out.push({ color: c, idx, pos }));
    }
    return out;
  }, [colors, tokens, kicked]);

  // winAmount computed inline where needed via calcWinAmount.
  const potPayout = config ? Math.floor(pot * (100 - config.platformFeePercent) / 100) : pot;

  // Cash-banner stack image mirrors the selected bet tier (Cash-1..4).
  const cashBannerImg = useMemo(() => {
    if (!selectedBet || !config?.betTiers?.length) return IMG.cash2;
    const idx = config.betTiers.findIndex((b) => b === selectedBet);
    return CASH_IMAGES[idx] || IMG.cash2;
  }, [selectedBet, config]);

  return (
    <div className="ludo-root fixed inset-0 z-[9999]">
      {/* Loading overlay — kept up until BOTH the WS auth has landed
          AND every preload URL has settled. Replaces the older
          in-lobby spinner that was gated only on authReady (so it
          vanished while half the lobby PNGs were still streaming
          over the wire — exactly the "loader hides too early" bug). */}
      <GameLoadingScreen
        visible={!authReady || !assetStatus.ready}
        theme="ludo"
        progress={assetStatus.progress}
      />
      <ConcurrentMatchModal
        open={concOpen}
        blocking={concBlocking}
        walletBalance={wallet}
        onResolved={() => {
          setConcOpen(false);
          setConcBlocking(null);
          toast.push('success', 'Active matches resolved — try again', 2000);
        }}
        onCancel={() => setConcOpen(false)}
      />
      <div className="app-container shadow-2xl">
        {/* Shared settings modal — see core/components/GameSettingsMenu.
            Originally each game had its own settings UI; per user
            request they were all consolidated into this one component
            so the toggles + exit affordance feel identical across
            games. */}
        <GameSettingsMenu
          open={settingsOpen}
          soundOn={soundEnabled}
          micMuted={micMuted}
          listenMuted={listenMuted}
          voiceEnabled={(config?.audioEnabled !== false) && (phase === 'playing')}
          onToggleSound={() => { playTap(); setSoundEnabled((s) => !s); }}
          onToggleMic={() => { playTap(); toggleMic(); }}
          onToggleListen={() => {
            // [J] Listen-all toggle — mirror PHP toggleListenAll() by
            // flipping el.muted on every remote <audio>. Keep React
            // state synced so the button icon flips too. Click is a
            // valid user gesture → nudge playback in case autoplay
            // was blocked earlier.
            playTap();
            setListenMuted((v) => {
              const next = !v;
              voiceRef.current?.setListenMuted(next);
              if (!next) voiceRef.current?.kickAudioPlayback();
              return next;
            });
          }}
          onExit={() => {
            playTap();
            setSettingsOpen(false);
            if (phase === 'playing' || phase === 'vs') {
              setQuitConfirm(true);
            } else {
              nav('/');
            }
          }}
          onClose={() => setSettingsOpen(false)}
          exitLabel={(phase === 'playing' || phase === 'vs') ? 'Exit Game' : 'Exit to Home'}
          anchor="left"
        />

        <TopBar
          walletAmt={wallet}
          onSettings={() => { playTap(); setSettingsOpen((o) => !o); }}
          onBack={() => {
            playTap();
            // Back has the same guard as the Exit Game menu item:
            // mid-match, prompt the quit-confirm (forfeits the bet);
            // otherwise navigate home.
            if (phase === 'playing' || phase === 'vs') {
              setQuitConfirm(true);
            } else {
              nav('/');
            }
          }}
          potPayout={phase === 'playing' ? potPayout : null}
          cashBannerImg={phase === 'playing' ? cashBannerImg : null}
        />

        {/* [K] Socket status chip — matches PHP #status-box verbatim:
            connecting → "Connecting to Server…"; lost → "Connection Lost!
            Reconnecting…"; reconnecting → "Reconnecting to Server…". Once
            authReady fires we hide the chip (PHP shows "Ready in Lobby"
            for 2 s then hides — we just hide immediately, same net UX). */}
        <div className="status-container">
          <div
            className={`status-box bg-gray-700 text-white ${
              authReady && wsStatus === 'open' ? 'hidden' : ''
            }`}
          >
            {wsStatus === 'lost'
              ? 'Connection Lost! Reconnecting…'
              : wsStatus === 'reconnecting'
                ? 'Reconnecting to Server…'
                : 'Connecting to Server…'}
          </div>
        </div>

        {/* ========== LOBBY ========== */}
        {phase === 'lobby' && (
          <LobbyBanner
            enabled={config?.lobbyBanner?.enabled}
            text={config?.lobbyBanner?.text}
            tone={config?.lobbyBanner?.tone}
          />
        )}
        {phase === 'lobby' && (
          <SetupScreen
            authReady={authReady}
            onlinePlayers={online}
            betTiers={config?.betTiers || []}
            selectedBet={selectedBet}
            selectedPlayers={selectedPlayers}
            platformFeePercent={config?.platformFeePercent || 0}
            waitingByBet={waitingByBet}
            waitingByBetAndPc={waitingByBetAndPc}
            showWaitingBadge={showWaitingBadge}
            onSelectPlayers={(n) => { playTap(); setSelectedPlayers(n); }}
            onSelectBet={(b) => { playTap(); setSelectedBet(b); handlePlayOnSelect(b); }}
          />
        )}

        {/* ========== WAITING MODAL ========== */}
        {phase === 'queue' && queue && selectedBet && (
          <WaitingModal
            current={queue.current}
            required={queue.required}
            bet={selectedBet}
            players={selectedPlayers}
            botActivateAt={botActivateAt}
            profiles={queue.profiles}
            yourUserId={queue.yourUserId}
            myAvatarUrl={user?.profile_image_url || null}
            onCancel={handleCancelQueue}
          />
        )}

        {/* ========== VS INTRO ========== */}
        {phase === 'vs' && vsIntroActive && (
          <VsScreen
            profiles={profiles}
            colors={colors}
            yourColor={yourColor}
            pot={pot}
            payout={potPayout}
          />
        )}

        {/* ========== GAME SCREEN ========== */}
        {phase === 'playing' && (
          <GameScreen
            boardTokens={boardTokens}
            currentTurn={currentTurn}
            yourColor={yourColor}
            validTokens={dice.valid}
            diceValue={dice.value}
            dicePlayer={dice.player}
            profiles={profiles}
            colors={colors}
            strikes={strikes}
            panelMap={panelMap}
            autoRoll={autoRoll}
            onToggleAutoRoll={() => setAutoRoll((v) => !v)}
            turnSeq={turnSeq}
            onRoll={handleRoll}
            onPickToken={handlePickToken}
            boardFx={boardFx}
            tokenDeco={tokenDeco}
            tripleSixCancel={tripleSixCancel}
            micMuted={micMuted}
            listenMuted={listenMuted}
            absent={absent}
            kicked={kicked}
          />
        )}

        {/* ========== RESULT ========== */}
        {phase === 'result' && result && (
          <ResultScreen
            kind={result.result}
            amount={result.result === 'win' ? result.payout : result.pot}
            winnerColor={result.winnerColor}
            platformFee={result.platformFee}
            onHome={() => nav('/')}
            onNewMatch={() => { setResult(null); setPhase('lobby'); }}
          />
        )}

        {/* ========== RECOVERY (loaded from /pending-result) ========== */}
        {recovery && phase === 'lobby' && (
          <RecoveryScreen
            kind={recovery.result}
            amount={recovery.result === 'win'
              ? (recovery.payout || 0)
              : (recovery.betAmount || recovery.pot || 0)}
            platformFee={recovery.platformFee || 0}
            opponents={recovery.opponents || []}
            onAck={acknowledgeRecovery}
          />
        )}

        {/* ========== QUIT CONFIRM ========== */}
        {quitConfirm && (
          <QuitConfirm
            amount={potPayout}
            onStay={() => setQuitConfirm(false)}
            onQuit={handleQuit}
          />
        )}

        {/* ========== INSUFFICIENT BALANCE ========== */}
        <InsufficientFundsModal
          open={insufficientOpen}
          required={insufficientBet}
          balance={wallet}
          onClose={() => setInsufficientOpen(false)}
        />
      </div>
    </div>
  );

  function handlePlayOnSelect(bet: number) {
    // Selecting a cash tile instantly starts matchmaking, mirroring the PHP behaviour.
    // (PHP flow: tap tile → open waiting modal → WS join_queue.)
    // IMPORTANT: unlock WebAudio *synchronously* here — this runs inside
    // the real user click, so AudioContext.resume() is honoured. The
    // previous setTimeout(() => handlePlay(), 0) deferred primeAudio()
    // off the gesture stack and iOS/Safari then silently blocked every
    // subsequent synth call ("completely muted" bug).
    //
    // We MUST pass `bet` through to handlePlay rather than letting it
    // re-read `selectedBet` from React state — the matching
    // setSelectedBet(bet) call in onSelectBet hasn't been applied yet
    // (state updates are batched until the next render), so reading
    // the state here would yield the PREVIOUS selection. Same goes
    // for `selectedPlayers` — we let handlePlay default to its state
    // value because that is set on a separate user gesture (the
    // 1v1/2v2 toggle) so it's never modified in the same click as
    // this call.
    primeAudio();
    handlePlay(bet);
  }
}

// ─────────────────────────────────────────────────────────────────────
// Top wallet + settings bar
// ─────────────────────────────────────────────────────────────────────
function TopBar({ walletAmt, onSettings, onBack, potPayout, cashBannerImg }: {
  walletAmt: number;
  onSettings: () => void;
  // Back button handler — renders as a small circular button to the
  // LEFT of the settings button. The parent wires the quit-confirm
  // logic (mid-match forfeits the bet; otherwise nav to home).
  onBack: () => void;
  // Non-null only during phase === 'playing': shows the active pot/payout
  // chip in the center of the TopBar. When null (lobby, queue, vs, result),
  // the bar reverts to its simple settings+wallet layout.
  potPayout: number | null;
  cashBannerImg: string | null;
}) {
  // Safe-area: the bar's gradient background extends up into the notch /
  // status-bar zone (no wrapper gutter), and paddingTop grows by the inset
  // so the clickable settings + wallet sit BELOW the notch. Net: zero wasted
  // vertical space in no-notch environments, auto-adjusts in notch devices.
  return (
    <div
      className="flex justify-between items-center gap-2 px-3 sm:px-4 pb-3 sm:pb-4 bg-gradient-to-b from-[#2e1656] to-[#461f74] shadow-md z-[150] w-full"
      style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
    >
      {/* Left cluster — back button (leftmost) then settings. Kept
          as one flex group so the shared `gap-2` in the parent only
          separates the cluster from the centre pot chip, not the
          two buttons from each other. */}
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onBack}
          aria-label="Back"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-black/30 border border-white/15 hover:bg-black/50 active:scale-95 transition text-white"
        >
          <Icon name="arrow_back" size={22} filled />
        </button>
        <button
          onClick={onSettings}
          aria-label="Settings"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-[0_0_5px_rgba(0,0,0,1)] hover:scale-105 active:scale-95 transition-transform"
        >
          <img src={IMG.settings} className="w-full h-full object-contain" alt="Settings" />
        </button>
      </div>

      {/* Center pot chip — only visible during active play. Copies the
          wallet chip's visual style exactly (same gradient, border, shadow,
          padding) so the TopBar stays consistent. Shows the bet-tier
          image in place of the coin, and "WIN" above the potential
          payout amount (stacked, no currency symbol). */}
      {potPayout != null && cashBannerImg && (
        <div className="flex-1 flex items-center justify-center min-w-0">
          <div
            className="bg-gradient-to-b from-[#29144d] to-[#37175c] rounded-lg flex items-center justify-center pe-1 sm:pe-[0.7rem] py-[0.3rem] sm:py-[0.5rem] border border-[#a0a0a021] shadow-[0_0_30px_rgba(255,255,255,0.05)] relative"
            title="Winner take-home (full pot)"
          >
            <img
              src={cashBannerImg}
              className="w-6 h-6 sm:w-7 sm:h-7 mr-2 relative scale-150 z-10"
              alt=""
            />
            <div className="flex flex-col items-start leading-none mr-1 sm:mr-2" id="top-pot-amt">
              <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.2em] text-white/70">
                WIN
              </span>
              <span className="text-white font-bold text-base sm:text-lg tracking-wide tabular-nums mt-0.5">
                {fmt(potPayout)}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-b from-[#29144d] to-[#37175c] rounded-lg shrink-0 flex items-center justify-center pe-1 sm:pe-[0.7rem] py-[0.3rem] sm:py-[0.5rem] border border-[#a0a0a021] shadow-[0_0_30px_rgba(255,255,255,0.05)] relative">
        <img src={IMG.coin} className="w-6 h-6 sm:w-7 sm:h-7 mr-2 relative scale-150 z-10" alt="Coin" />
        <span className="text-white font-bold text-base sm:text-lg mr-2 sm:mr-3 tracking-wide" id="wallet-amt">
          {fmt(walletAmt)}
        </span>
        <button className="rounded-sm flex items-center justify-center hover:scale-110 active:scale-95 transition-transform">
          <img src={IMG.plus} className="w-6 h-6 sm:w-7 sm:h-7 object-contain" alt="Add" />
        </button>
      </div>
    </div>
  );
}

// NOTE: the old local `SettingsMenu` component used to live here, with
// its own AnimatePresence-driven panel. It has been replaced by the
// shared `core/components/GameSettingsMenu` so every game presents an
// identical settings affordance. The surrounding Ludo lobby/game code
// still provides the state (sound / mic / listen / exit) and passes
// it into the shared component at render time.

// ─────────────────────────────────────────────────────────────────────
// Setup / Lobby Screen
// ─────────────────────────────────────────────────────────────────────
function SetupScreen({
  authReady, onlinePlayers, betTiers, selectedBet, selectedPlayers, platformFeePercent,
  waitingByBetAndPc,
  waitingByBet, showWaitingBadge,
  onSelectPlayers, onSelectBet,
}: {
  authReady: boolean;
  onlinePlayers: number;
  betTiers: number[];
  selectedBet: number | null;
  selectedPlayers: 2 | 4;
  platformFeePercent: number;
  // Real-human queue counts keyed by stake amount, plus the admin
  // toggle that lets an operator hide the badge entirely (e.g., during
  // off-peak hours when "0 waiting" would look dead).
  waitingByBet: Record<string, number>;
  // Per-(bet, playerCount) version of the same data — what the badge
  // actually keys off so 2P viewers don't see badges from 4P queues
  // (and vice versa). See the comment block above the matching state
  // in the parent for the full rationale.
  waitingByBetAndPc: Record<string, Record<string, number>>;
  showWaitingBadge: boolean;
  onSelectPlayers: (n: 2 | 4) => void;
  onSelectBet: (n: number) => void;
}) {
  // Render every admin-configured bet tier — no slice. Earlier this
  // capped at the first 4, which silently hid any 5th+ tier. With
  // the live config `BET_TIERS = [0, 100, 500, 1500, 3000]` (5
  // entries), the 5th card was invisible — anyone who somehow
  // joined that hidden tier had their waiting count broadcast to a
  // card that didn't render. Other games (XO, Carrom) already render
  // the full list; Ludo now matches.
  const cashes = betTiers;

  return (
    <div
      id="setup-screen"
      className="flex flex-col w-full h-full relative transition-all duration-300 flex-grow goldern_border rounded-none sm:rounded-b-3xl"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {!authReady && (
        <div id="lobby-loader" className="lobby-loader">
          <div className="ludo-spinner" aria-hidden="true">
            <div className="ludo-spinner-token red" />
            <div className="ludo-spinner-token green" />
            <div className="ludo-spinner-token yellow" />
            <div className="ludo-spinner-token blue" />
          </div>
          <div className="lobby-loader-text">Setting up lobby</div>
          <div className="lobby-loader-bar" />
        </div>
      )}

      {authReady && (
        <div id="lobby-content" className="flex flex-col flex-grow w-full">
          <div className="flex-grow flex flex-col justify-center items-center px-4 sm:px-6 min-h-[150px]">
            <img
              src={IMG.logo}
              className="w-full h-full object-contain drop-shadow-2xl"
              style={{ maxHeight: 250 }}
              alt="Ludo Cash Logo"
            />
            <div className="mt-4 text-[#dfff7a] text-[10px] sm:text-xs font-bold tracking-widest bg-black/50 px-4 py-2 rounded-full border border-[#bc9648] flex items-center justify-center gap-2">
              <span
                className="inline-block w-2 h-2 rounded-full bg-[#22c55e] shadow-[0_0_6px_rgba(34,197,94,0.8)]"
                aria-hidden
              />
              <span><span id="live-players-count">{onlinePlayers}</span> PLAYERS ONLINE</span>
            </div>
          </div>

          <div className="px-4 sm:px-5 mt-auto flex flex-col gap-3 w-full pb-6">
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-2 sm:mb-4">
              <PawnButton n={2} img={IMG.pawns2} selected={selectedPlayers === 2} onClick={() => onSelectPlayers(2)} />
              <PawnButton n={4} img={IMG.pawns4} selected={selectedPlayers === 4} onClick={() => onSelectPlayers(4)} />
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {cashes.map((val, i) => {
                const winAmt = Math.floor(val * selectedPlayers * (100 - platformFeePercent) / 100);
                // Look up real humans queued at THIS exact (bet,
                // playerCount). JSON keys come back as strings.
                // `waiting_by_bet` (cross-pc rollup) is intentionally
                // not used here — we only highlight cards where the
                // user clicking would actually JOIN the existing
                // queue, not spawn a new pc-mismatched one.
                const waiting = Number(
                  waitingByBetAndPc?.[String(val)]?.[String(selectedPlayers)] || 0,
                );
                const showBadge = showWaitingBadge && waiting > 0;
                return (
                  <button
                    key={val}
                    data-val={val}
                    onClick={() => onSelectBet(val)}
                    className={[
                      'cash-btn relative w-full py-3 sm:h-[95px] rounded-xl border-[3px] border-[#bc9648]',
                      'bg-gradient-to-b from-[#2a0b4e] to-[#14032a] flex flex-row items-center justify-between',
                      'pl-3 sm:pl-4 pr-1 sm:pr-2 shadow-[0_6px_15px_rgba(0,0,0,0.6)]',
                      selectedBet === val ? 'selected' : '',
                    ].join(' ')}
                  >
                    {showBadge && <WaitingBadge count={waiting} />}
                    <div className="flex flex-col items-start justify-center pt-1 pointer-events-none">
                      <span className={
                        'text-white font-black leading-none text-shadow-heavy tracking-tighter '
                        + (val === 0 ? 'text-[22px] sm:text-[30px]' : 'text-[32px] sm:text-[42px]')
                      }>{val === 0 ? 'FREE' : val}</span>
                      <span className="cash-win-label text-[#dfff7a] font-bold text-[10px] sm:text-sm tracking-widest text-shadow-heavy mt-1">
                        {val === 0 ? 'PLAY FOR FUN' : `WIN ${winAmt}`}
                      </span>
                    </div>
                    <img
                      src={CASH_IMAGES[i] || CASH_IMAGES[CASH_IMAGES.length - 1]}
                      className="w-[50px] h-[50px] sm:w-[75px] sm:h-[75px] object-contain drop-shadow-lg pointer-events-none"
                      alt=""
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Small "N waiting" pill shown on the top-right of a cash button when
// real players are currently queued at that stake. `count` is the
// humans-only number from the server (bots are never included). The
// badge floats slightly outside the button's border-radius to read as
// a "flag on top" rather than a number baked into the tile.
function WaitingBadge({ count }: { count: number }) {
  return (
    <span
      className="cash-waiting-badge"
      aria-label={`${count} waiting`}
      title={`${count} waiting at this stake`}
    >
      <svg
        width="10" height="10" viewBox="0 0 24 24"
        fill="currentColor" aria-hidden="true"
        className="cash-waiting-badge__icon"
      >
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
      </svg>
      <span className="cash-waiting-badge__num">{count}</span>
      <span className="cash-waiting-badge__label">waiting</span>
    </span>
  );
}

function PawnButton({ n, img, selected, onClick }: { n: 2 | 4; img: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      data-players={n}
      onClick={onClick}
      className={[
        'pawn-btn relative w-full aspect-[4/3] rounded-xl border-[3px] border-[#bc9648]',
        'bg-gradient-to-b from-[#2a0b4e] to-[#14032a] flex flex-col justify-center items-center',
        'shadow-[0_6px_15px_rgba(0,0,0,0.6)]',
        selected ? 'selected' : '',
      ].join(' ')}
    >
      <div className="flex items-center justify-center w-full px-2 mt-1 gap-1">
        <span className="text-[#e8c66e] text-5xl sm:text-6xl font-black text-shadow-heavy leading-none">{n}</span>
        <img src={img} className="w-[60px] h-[60px] sm:w-[80px] sm:h-[80px] object-contain drop-shadow-xl" alt={`${n} Pawns`} />
      </div>
      <span className={`font-bold text-[12px] sm:text-[15px] tracking-widest mt-1 sm:mt-2 text-shadow-heavy ${selected ? 'text-[#dfff7a]' : 'text-[#bc9648]'}`}>
        {n === 2 ? 'TWO PLAYER' : 'FOUR PLAYER'}
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Waiting Modal — flipping-avatar slots locked as players join.
// ─────────────────────────────────────────────────────────────────────
function WaitingModal({
  current, required, bet, players, botActivateAt, onCancel,
  profiles, yourUserId, myAvatarUrl,
}: {
  current: number; required: number; bet: number; players: 2 | 4;
  botActivateAt: number | null;
  onCancel: () => void;
  // Full profile list for already-joined users (real or backfilled).
  // Order mirrors the server's `q.users` join order; index 0 is the
  // FIRST joiner (not necessarily "me"). We label the current viewer
  // by matching `yourUserId`.
  profiles: Array<{ userId: string; name: string; avatarUrl: string | null; is_bot: boolean }>;
  yourUserId: string | null;
  // Fallback used for the "me" slot when the server hasn't yet echoed
  // the first waiting_in_queue (optimistic render between join and
  // server ack).
  myAvatarUrl: string | null;
}) {
  const needed = Math.max(0, required - current);
  const label = needed === 1 ? 'more player' : 'more players';

  // Slot colours are purely decorative here — the actual in-game
  // colour is assigned by the server at session-start (randomised).
  const slotColors: Color[] = players === 2 ? ['blue', 'green'] : ['blue', 'red', 'green', 'yellow'];

  // Rotate through avatar sources for the searching animation (image flip).
  const [flipIdx, setFlipIdx] = useState(0);
  // Animated "." "‥" "…" cycle for unlocked slot labels — pseudo-element
  // keyframe approach didn't render (CSS `content` doesn't animate on the
  // actual <div>), so we drive it from React state instead.
  const [dotPhase, setDotPhase] = useState(0);
  // Countdown to server-provided bot activation deadline.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Match PHP slot flipper cadence (450ms) — the faster flip is what
    // sells the "searching for opponents" feel; 900ms felt sluggish.
    const t = setInterval(() => setFlipIdx((i) => (i + 1) % 4), 450);
    const d = setInterval(() => setDotPhase((p) => (p + 1) % 3), 400);
    const tick = setInterval(() => setNow(Date.now()), 500);
    return () => { clearInterval(t); clearInterval(d); clearInterval(tick); };
  }, []);
  const dotsText = dotPhase === 0 ? '.' : dotPhase === 1 ? '..' : '...';
  const secsToBot = botActivateAt ? Math.max(0, Math.ceil((botActivateAt - now) / 1000)) : null;
  const avatarChoices = [
    '/games/ludo/RC_Player_red.png',
    '/games/ludo/RC_Player_green.png',
    '/games/ludo/RC_Player_yellow.png',
    '/games/ludo/RC_Player_blue.png',
  ];

  return (
    <div
      id="waiting-modal"
      className="absolute inset-0 z-[160] bg-black/85 flex justify-center items-center transition-opacity duration-300"
    >
      <div className="relative w-[85%] max-w-[340px] border-[3px] border-[#d4af37] bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-[#4a126b] via-[#2a0845] to-[#150226] rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.9)] flex flex-col items-center">
        <button
          onClick={onCancel}
          className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-b from-[#e63946] to-[#900c16] border-2 border-[#d4af37] rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg hover:scale-110 active:scale-95 transition-transform"
        >
          ✕
        </button>

        <img src={IMG.logo} className="w-28 h-auto object-contain drop-shadow-2xl mb-1" alt="Logo" />

        <h2 className="text-gold-gradient text-xl font-black tracking-widest gold-text-shadow mb-2 text-center uppercase">
          <span className="block text-sm tracking-[0.3em] text-[#bc9648]">WAITING FOR</span>
          <span
            className="block text-6xl md:text-7xl font-black leading-none my-1 text-shadow-heavy"
            style={{ color: '#dfff7a', textShadow: '0 0 14px rgba(223,255,122,0.45)' }}
          >
            {needed}
          </span>
          <span className="block text-[11px] tracking-[0.25em] text-[#f8e7b3] uppercase">{label}</span>
        </h2>

        <div id="wait-slots-row" className="wait-slots-row">
          {slotColors.map((c, i) => {
            const locked = i < current;
            // Resolve the profile for this locked slot. If the server
            // hasn't echoed our own profile yet (first-frame race) we
            // fall back to the auth context's avatar + username for
            // the first slot.
            const p = locked ? (profiles[i] || null) : null;
            const isMe = locked && (
              (yourUserId && p && p.userId === yourUserId) ||
              (!p && i === 0)
            );
            // Avatar source: real player avatar if provided by server;
            // else the auth fallback for self; else the animated
            // colour-pawn flipper for empty slots. Bots are shown with
            // their assigned colour pawn — no visual distinction from
            // a real player who happens not to have uploaded a photo.
            let avatar: string;
            if (locked && p) {
              avatar = p.avatarUrl || `/games/ludo/RC_Player_${c}.png`;
            } else if (locked && isMe && myAvatarUrl) {
              avatar = myAvatarUrl;
            } else if (locked) {
              avatar = `/games/ludo/RC_Player_${c}.png`;
            } else {
              avatar = avatarChoices[(flipIdx + i) % avatarChoices.length];
            }
            // Name label under the slot: real player's name when known,
            // "YOU" for the current viewer, otherwise the colour name
            // as a decorative fallback.
            const nameLabel = locked
              ? (isMe ? 'YOU' : (p?.name || c.toUpperCase()))
              : dotsText;
            return (
              <div
                key={c}
                className={['wait-slot', locked ? 'locked' : 'searching', locked && isMe ? 'me' : ''].join(' ')}
                style={{ ['--slot-color' as any]: COLOR_CONFIG[c].hex }}
              >
                {locked && isMe && <div className="wait-slot-you">YOU</div>}
                <div className="wait-slot-ring">
                  <img src={avatar} alt="" />
                </div>
                <div className="wait-slot-label">{nameLabel}</div>
              </div>
            );
          })}
        </div>

        <div className="w-full bg-[#1e053a] border border-[#bc9648] rounded-lg py-2 px-3 mb-3 shadow-inner flex justify-around items-center">
          <div className="flex items-center gap-2">
            <img src={players === 2 ? IMG.pawns2 : IMG.pawns4} className="w-7 h-7 object-contain" alt="" />
            <span className="text-[#dfff7a] font-bold text-[10px] tracking-wider">{players} PLAYERS</span>
          </div>
          <span className="text-[#d4af37] text-[10px] font-black tracking-widest">FOR</span>
          <div className="flex items-center gap-2">
            <img src={IMG.cash2} className="w-7 h-7 object-contain" alt="" />
            <span className="text-[#7ae2ff] font-bold text-[10px] tracking-wider">{fmt(bet)}</span>
          </div>
        </div>

        {secsToBot != null && secsToBot > 0 && (
          <div className="text-[11px] tracking-widest text-[#f8e7b3] mb-2 text-center">
            MATCH STARTS IN <span className="text-[#dfff7a] font-bold">{secsToBot}s</span>
          </div>
        )}

        <button
          onClick={onCancel}
          className="w-full py-3 rounded-lg border-2 border-[#bc9648] bg-gradient-to-b from-[#e63946] to-[#900c16] text-white font-black tracking-widest text-shadow-heavy shadow-md hover:brightness-110 active:scale-[0.98] transition-all"
        >
          CANCEL
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// VS Intro (3-second cinematic)
// ─────────────────────────────────────────────────────────────────────
function VsScreen({ profiles, colors, yourColor, pot, payout }: {
  profiles: Record<string, Profile>; colors: Color[]; yourColor: Color | null; pot: number; payout: number;
}) {
  const mine = yourColor;
  const others = colors.filter((c) => c !== mine);

  // Floating gold sparks — randomised once per mount.
  const sparks = useMemo(() => (
    Array.from({ length: 12 }).map((_, i) => ({
      left: Math.random() * 100,
      bottom: -Math.random() * 20,
      delay: Math.random() * 2,
      duration: 2.4 + Math.random() * 1.6,
      key: i,
    }))
  ), []);

  return (
    <div id="vs-screen" className="overlay-screen themed z-[250]">
      <div id="vs-sparks" className="vs-sparks">
        {sparks.map((s) => (
          <span
            key={s.key}
            className="vs-spark"
            style={{ left: `${s.left}%`, bottom: `${s.bottom}%`, animationDelay: `${s.delay}s`, animationDuration: `${s.duration}s` }}
          />
        ))}
      </div>

      <div className="vs-banner">Match Found</div>

      {/* Layout matches the PHP reference (ludo_client.php L3425):
          [ME (left, elevated, YOU badge)]  V/S  [opponent1] [opponent2] ...
          ME is always on the left and all opponents flow in from the right. */}
      <div id="vs-stage" className="vs-stage">
        {mine && (
          <VsCard color={mine} profile={profiles[mine]} side="left-in" isMe />
        )}

        <div className="vs-divider"><span>V/S</span></div>

        {others.map((c, i) => (
          <VsCard
            key={c}
            color={c}
            profile={profiles[c]}
            side={i % 2 === 0 ? 'right-in' : 'left-in'}
          />
        ))}
      </div>

      <div className="vs-pot">
        POT
        <span id="vs-pot-amount" className="ml-2 text-[#fff6d6]">{fmt(pot)} · WIN{fmt(payout)}</span>
      </div>
    </div>
  );
}

function VsCard({ color, profile, side, isMe }: { color: Color; profile?: Profile; side: string; isMe?: boolean }) {
  const hex = COLOR_CONFIG[color].hex;
  return (
    <div
      className={`vs-card ${isMe ? 'me' : side}`}
      style={{ ['--vs-color' as any]: hex }}
    >
      {isMe && <div className="vs-you-badge">YOU</div>}
      <div className="vs-card-ring">
        <img src={profile?.avatarUrl || `/games/ludo/RC_Player_${color}.png`} alt="" />
      </div>
      <div className="vs-card-name">{profile?.name || profile?.displayName || color}</div>
      <span className="vs-card-color">{color}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Game Screen — board + 4 player panels + cash banner.
// ─────────────────────────────────────────────────────────────────────
function GameScreen({
  boardTokens, currentTurn, yourColor, validTokens, diceValue, dicePlayer,
  profiles, colors, strikes, panelMap,
  autoRoll, onToggleAutoRoll, turnSeq,
  onRoll, onPickToken, boardFx, tokenDeco,
  tripleSixCancel, micMuted, listenMuted, absent, kicked,
}: {
  boardTokens: BoardToken[];
  currentTurn: Color | null;
  yourColor: Color | null;
  validTokens: number[];
  diceValue: number | null;
  dicePlayer: Color | null;
  profiles: Record<string, Profile>;
  colors: Color[];
  strikes: Record<string, number>;
  panelMap: { bl: Color; tl: Color; tr: Color; br: Color };
  autoRoll: boolean;
  onToggleAutoRoll: () => void;
  turnSeq: number;
  onRoll: () => void;
  onPickToken: (c: Color, i: number) => void;
  boardFx: BoardFx[];
  tokenDeco: TokenDeco;
  // Color whose third-six was just forfeited (null when no forfeit is
  // being shown). Propagates to that player's DiceButton to render a ❌.
  tripleSixCancel: Color | null;
  // Voice state threaded to the local player's panel only.
  micMuted: boolean;
  listenMuted: boolean;
  // Seats whose WebSocket is currently dropped — forwarded to each
  // PlayerPanel so the opponent row can render as greyed-out "away".
  absent: Set<string>;
  // Seats that were kicked / quit mid-match. Terminal: panel shows
  // an empty "Left" state, tokens already filtered out of boardTokens.
  kicked: Set<string>;
}) {
  return (
    <div
      id="game-screen"
      className="flex flex-col w-full h-full flex-grow goldern_border rounded-none sm:rounded-b-3xl relative z-[10] bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#7b168b] via-[#3e1265] to-[#1a092e]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Cash banner removed — pot/payout now lives in the TopBar center
          chip during active play. Frees ~48px of vertical board space. */}

      {/* Board + player panels */}
      <div className="flex flex-col flex-grow justify-center w-full max-w-[430px] mx-auto gap-1 sm:gap-2">
        {/* Top players */}
        <div className="w-full px-2 flex justify-between z-20">
          <div id="pos-tl" className="w-[48%] flex flex-col">
            {colors.includes(panelMap.tl) && (
              <PlayerPanel
                color={panelMap.tl}
                profile={profiles[panelMap.tl]}
                isMe={yourColor === panelMap.tl}
                isTurn={currentTurn === panelMap.tl}
                strikes={strikes[panelMap.tl] || 0}
                position="top-left"
                diceValue={dicePlayer === panelMap.tl ? diceValue : null}
                canRoll={currentTurn === panelMap.tl && yourColor === panelMap.tl && diceValue == null}
                autoRoll={autoRoll}
                onToggleAutoRoll={onToggleAutoRoll}
                turnSeq={turnSeq}
                onRoll={onRoll}
                tripleSixCancel={tripleSixCancel}
                micMuted={micMuted}
                listenMuted={listenMuted}
                absent={absent.has(panelMap.tl)}
                kicked={kicked.has(panelMap.tl)}
              />
            )}
          </div>
          <div id="pos-tr" className="w-[48%] flex flex-col">
            {colors.includes(panelMap.tr) && (
              <PlayerPanel
                color={panelMap.tr}
                profile={profiles[panelMap.tr]}
                isMe={yourColor === panelMap.tr}
                isTurn={currentTurn === panelMap.tr}
                strikes={strikes[panelMap.tr] || 0}
                position="top-right"
                diceValue={dicePlayer === panelMap.tr ? diceValue : null}
                canRoll={currentTurn === panelMap.tr && yourColor === panelMap.tr && diceValue == null}
                autoRoll={autoRoll}
                onToggleAutoRoll={onToggleAutoRoll}
                turnSeq={turnSeq}
                onRoll={onRoll}
                tripleSixCancel={tripleSixCancel}
                micMuted={micMuted}
                listenMuted={listenMuted}
                absent={absent.has(panelMap.tr)}
                kicked={kicked.has(panelMap.tr)}
              />
            )}
          </div>
        </div>

        <Board
          tokens={boardTokens}
          yourColor={yourColor}
          currentTurn={currentTurn}
          validTokens={validTokens}
          onPickToken={onPickToken}
          fx={boardFx}
          deco={tokenDeco}
        />

        {/* Bottom players */}
        <div className="w-full px-2 flex justify-between z-20">
          <div id="pos-bl" className="w-[48%] flex flex-col">
            {colors.includes(panelMap.bl) && (
              <PlayerPanel
                color={panelMap.bl}
                profile={profiles[panelMap.bl]}
                isMe={yourColor === panelMap.bl}
                isTurn={currentTurn === panelMap.bl}
                strikes={strikes[panelMap.bl] || 0}
                position="bottom-left"
                diceValue={dicePlayer === panelMap.bl ? diceValue : null}
                canRoll={currentTurn === panelMap.bl && yourColor === panelMap.bl && diceValue == null}
                autoRoll={autoRoll}
                onToggleAutoRoll={onToggleAutoRoll}
                turnSeq={turnSeq}
                onRoll={onRoll}
                tripleSixCancel={tripleSixCancel}
                micMuted={micMuted}
                listenMuted={listenMuted}
                absent={absent.has(panelMap.bl)} kicked={kicked.has(panelMap.bl)}
              />
            )}
          </div>
          <div id="pos-br" className="w-[48%] flex flex-col">
            {colors.includes(panelMap.br) && (
              <PlayerPanel
                color={panelMap.br}
                profile={profiles[panelMap.br]}
                isMe={yourColor === panelMap.br}
                isTurn={currentTurn === panelMap.br}
                strikes={strikes[panelMap.br] || 0}
                position="bottom-right"
                diceValue={dicePlayer === panelMap.br ? diceValue : null}
                canRoll={currentTurn === panelMap.br && yourColor === panelMap.br && diceValue == null}
                autoRoll={autoRoll}
                onToggleAutoRoll={onToggleAutoRoll}
                turnSeq={turnSeq}
                onRoll={onRoll}
                tripleSixCancel={tripleSixCancel}
                micMuted={micMuted}
                listenMuted={listenMuted}
                absent={absent.has(panelMap.br)}
                kicked={kicked.has(panelMap.br)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PlayerPanel — one player's profile card with strikes + dice slot.
// ─────────────────────────────────────────────────────────────────────
function PlayerPanel({
  color, profile, isMe, isTurn, strikes, position, diceValue, canRoll,
  autoRoll, onToggleAutoRoll, turnSeq, onRoll, tripleSixCancel,
  micMuted, listenMuted, absent, kicked,
}: {
  color: Color;
  profile?: Profile;
  isMe: boolean;
  isTurn: boolean;
  strikes: number;
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  diceValue: number | null;
  canRoll: boolean;
  autoRoll: boolean;
  onToggleAutoRoll: () => void;
  turnSeq: number;
  onRoll: () => void;
  tripleSixCancel: Color | null;
  // Live voice-state so the local player's card shows mic + hearing
  // glyphs next to the YOU badge (icons present = enabled). Ignored
  // on opponent panels (only rendered when isMe).
  micMuted: boolean;
  listenMuted: boolean;
  // True while the seat is inside the disconnect-grace window — greys
  // the card so the other players can see "this seat is temporarily
  // away" instead of guessing from a missing voice stream.
  absent: boolean;
  // True once the seat has been kicked / quit for good. Renders an
  // empty-seat card so the quitter's avatar, name, strikes, and
  // turn-indicator all disappear from the other players' views.
  kicked: boolean;
}) {
  const showCancel = tripleSixCancel === color;
  const isTop = position.startsWith('top');
  const isLeft = position.endsWith('left');
  const displayName = profile?.name || profile?.displayName || color;
  const glowClass = isTurn && !absent && !kicked ? `glow-${color}` : '';
  const arrowShadow = `drop-shadow(0 0 5px var(--${color}))`;

  // Terminal "Left" state — render an empty-seat placeholder in the
  // position the quitter used to occupy. We keep the outer wrapper in
  // place so the 4-panel grid doesn't reflow; everything inside (avatar,
  // name, strikes, dice, timer, turn-indicator) is stripped.
  if (kicked) {
    return (
      <div className="w-full flex flex-col items-center">
        <div className={`timer-container w-[95%] ${isTop ? 'order-last' : 'order-first'}`}
             style={{ visibility: 'hidden' }}>
          <div className="timer-fill" />
        </div>
        <div className="player-profile slim_goldern_border player-profile--kicked"
             id={`prof-card-${color}`}
             aria-label={`Seat ${color} left the match`}>
          <div className="flex-1 text-center text-[11px] uppercase tracking-widest text-neutral-400 py-4">
            Left the match
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center">
      {/* Timer bar — animates from 100%→0% and white→pink over 16s when it's this player's turn. */}
      <div
        className={`timer-container w-[95%] ${isTop ? 'order-last' : 'order-first'}`}
        id={`timer-${color}`}
        style={{ visibility: isTurn ? 'visible' : 'hidden' }}
      >
        {isTurn
          ? <TimerFill key={`${color}-${turnSeq}`} />
          : <div className="timer-fill" id={`timer-fill-${color}`} />}
      </div>

      <div
        className={[
          'player-profile slim_goldern_border relative',
          glowClass,
          isMe && isTurn ? 'is-my-turn' : '',
          absent ? 'player-profile--absent' : '',
        ].filter(Boolean).join(' ')}
        id={`prof-card-${color}`}
      >
        {/* Bouncing arrow for left-column players (points right, into the card). */}
        {isLeft && isTurn && (
          <div
            className="absolute -right-6 top-1/2 animate-bounce-r z-50"
            style={{ color: `var(--${color})`, filter: arrowShadow }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="21,9 10,9 10,4 2,12 10,20 10,15 21,15" />
            </svg>
          </div>
        )}
        {!isLeft && isTurn && (
          <div
            className="absolute -left-6 top-1/2 animate-bounce-l z-50"
            style={{ color: `var(--${color})`, filter: arrowShadow }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="3,9 14,9 14,4 22,12 14,20 14,15 3,15" />
            </svg>
          </div>
        )}

        <div className="flex flex-col items-center w-[55px] shrink-0">
          <div className={`avatar-circle slim_goldern_border avatar-${color}`}>
            <img
              src={profile?.avatarUrl || `/games/ludo/RC_Player_${color}.png`}
              alt=""
            />
          </div>
          <div className="strikes-container mt-1" id={`strikes-${color}`}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className={`strike-dot ${i < strikes ? 'active' : ''}`} />
            ))}
          </div>
        </div>

        {/* Middle column — name + meta. Name always truncates (`min-w-0`
            + `truncate`) so a long display name can never push on the
            dice slot. YOU badge sits BELOW the name so the card's
            vertical rhythm stays stable regardless of name length. */}
        <div className="flex flex-col justify-center flex-grow pl-1 min-w-0 overflow-hidden z-10 min-h-[50px]">
          <span className="text-[11px] text-white font-bold leading-tight truncate uppercase w-full block" id={`name-${color}`}>
            {displayName}
          </span>
          {isMe && (
            <span className="mt-1 flex items-center gap-1 w-max leading-none">
              <span className="bg-white text-black px-1.5 py-0.5 rounded text-[8px] font-black animate-pulse">
                YOU
              </span>
              {/* Mic + listen status glyphs. Visible when enabled, absent
                  when muted. Kept as inline SVGs (not emojis) so the
                  colour + size can follow the text palette exactly
                  without platform-specific emoji rendering artefacts. */}
              {/* Mic + listen glyphs — only rendered when the state is
                  ENABLED (present = on, absent = off). Material Symbols
                  via the shared <Icon> component so future theme
                  changes carry across the platform. */}
              {!micMuted && (
                <Icon
                  name="mic"
                  size={12}
                  filled
                  ariaLabel="Mic on"
                  className="text-white/90 drop-shadow-[0_0_2px_rgba(0,0,0,0.6)]"
                />
              )}
              {!listenMuted && (
                <Icon
                  name="headphones"
                  size={12}
                  filled
                  ariaLabel="Hearing others"
                  className="text-white/90 drop-shadow-[0_0_2px_rgba(0,0,0,0.6)]"
                />
              )}
            </span>
          )}
          {!isMe && (
            <div className="flex items-center mt-1">
              <span className="text-[10px] text-yellow-400 font-bold truncate">
                {profile?.wallet != null ? fmt(profile.wallet) : ''}
              </span>
            </div>
          )}
        </div>

        {/* Dice column — locked to 44×44 (shrink-0) so name length never
            squeezes it into a rectangle. Below the dice sits the
            auto-roll toggle (only visible on the local player's panel)
            — a larger, labelled "AUTO" pill that's easier to hit than
            the old in-card checkbox. Only renders during our own turn
            so it never reveals which seat is ours to opponents looking
            over the shoulder. */}
        <div className="flex flex-col items-center ml-auto gap-1 shrink-0">
          <div className={`dice-slot relative ${isTurn ? '' : 'hidden'}`} id={`dice-slot-${color}`}>
            <DiceButton
              color={color}
              value={diceValue}
              waiting={canRoll && diceValue == null}
              disabled={!canRoll}
              onClick={onRoll}
            />
            {/* Triple-six forfeit overlay — covers the dice face with
                a red ❌ for ~1.5s while the server advances the turn,
                paired with a cancel-buzzer sound. */}
            {showCancel && (
              <div className="absolute inset-0 z-[60] flex items-center justify-center rounded-md pointer-events-none bg-black/40 backdrop-blur-[1px]">
                <span className="text-red-500 font-black text-4xl leading-none drop-shadow-[0_0_6px_rgba(0,0,0,0.9)] animate-pulse">
                  ✕
                </span>
              </div>
            )}
          </div>
          {isMe && isTurn && (
            <button
              type="button"
              onClick={onToggleAutoRoll}
              aria-pressed={autoRoll}
              aria-label={`Auto-roll ${autoRoll ? 'on' : 'off'}`}
              title={`Auto-roll — ${autoRoll ? 'ON' : 'OFF'} (tap to toggle)`}
              className={[
                // Width locked to the dice slot (44px) so the pill never
                // pushes on the middle text column. State is conveyed
                // by fill colour, not extra text (ON/OFF words made the
                // pill too wide and ate into the player name).
                'w-[44px] text-[9px] font-black tracking-[0.1em] leading-none py-[3px] rounded-full border transition-colors select-none text-center',
                autoRoll
                  ? 'bg-[#67e67a] text-black border-[#2e8b3b] shadow-[0_0_6px_rgba(103,230,122,0.55)]'
                  : 'bg-[#1c1030] text-[#8a8a8a] border-[#5a4a7a] hover:bg-[#2a1847]',
              ].join(' ')}
            >
              AUTO
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Animated timer bar — on mount, snaps to 100% white, then transitions to
// 0% width and pink over 16s. The parent uses `key={turnSeq}` so this
// component remounts on every turn change and the animation restarts.
function TimerFill() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Start state — full width, white.
    el.style.width = '100%';
    el.style.backgroundColor = '#ffffff';
    el.style.transition = 'none';
    // Force reflow so the next style set produces a transition.
    void el.offsetWidth;
    el.style.transition = 'width 16s linear, background-color 16s linear';
    el.style.width = '0%';
    el.style.backgroundColor = '#ef447a';
  }, []);
  return <div ref={ref} className="timer-fill" />;
}

function DiceButton({ color, value, waiting, disabled, onClick }: {
  color: Color; value: number | null; waiting: boolean; disabled: boolean; onClick: () => void;
}) {
  // When the dice goes from "no value" to a number, briefly show the tumble
  // animation (400ms) before settling on the face — matches PHP rollDice().
  const [tumbling, setTumbling] = useState(false);
  const prevValue = useRef<number | null>(null);
  useEffect(() => {
    if (prevValue.current == null && value != null) {
      setTumbling(true);
      const t = setTimeout(() => setTumbling(false), 400);
      prevValue.current = value;
      return () => clearTimeout(t);
    }
    if (value == null) setTumbling(false);
    prevValue.current = value;
  }, [value]);

  const classes = [
    'dice-btn', `dice-${color}`,
    waiting ? 'waiting-to-roll' : '',
    tumbling ? 'tumble' : '',
    !tumbling && value != null ? `face-${value}` : '',
    'absolute inset-0 w-full h-full border-none rounded-md',
  ].filter(Boolean).join(' ');
  return (
    <button id={`dice-${color}`} className={classes} onClick={onClick} disabled={disabled}>
      <span className="roll-text">ROLL</span>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => <div key={n} className={`dot dot-${n}`} />)}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Result / Recovery / Quit
// ─────────────────────────────────────────────────────────────────────
function ResultScreen({ kind, amount, winnerColor, platformFee, onHome, onNewMatch }: {
  kind: 'win' | 'loss' | 'kick';
  amount: number;
  winnerColor?: string;
  platformFee?: number;
  onHome: () => void;
  onNewMatch: () => void;
}) {
  const icon = kind === 'win' ? IMG.victory : kind === 'kick' ? IMG.kicked : IMG.defeat;
  // [I] win-state adds the golden glow + trophy ring; lose-state tints red;
  // kicked-state uses the muted boot styling. Matches PHP
  // card.classList.add('win-state' | 'lose-state' | 'kicked-state').
  const stateClass = kind === 'win' ? 'win-state' : kind === 'loss' ? 'lose-state' : 'kicked-state';
  const title = kind === 'win' ? 'YOU WON!' : kind === 'kick' ? 'KICKED OUT' : 'YOU LOST';
  // [I] Subtitle matches PHP verbatim: winner colour is broadcast in the
  // game_over event so we can name the colour that took the crown, not a
  // generic "glory is yours" string.
  const winColor = (winnerColor || '').toUpperCase();
  const sub = kind === 'win'
    ? (winColor ? `${winColor} TAKES THE CROWN` : 'Glory is yours')
    : kind === 'loss'
      ? (winColor ? `${winColor} WON THE POT` : 'Better luck next time')
      : 'You were removed from the match';
  // [I] PHP uses "You Won" for win and "Pot Claimed" for loss (shows the
  // full pot that was taken from the table), "You Lost" for kicked.
  const amountLabel = kind === 'win' ? 'You Won' : kind === 'loss' ? 'Pot Claimed' : 'You Lost';

  return (
    <div id="result-screen" className="overlay-screen">
      {kind === 'win' && <CoinRain />}
      <div className={`result-card ${stateClass}`}>
        <div className="result-rays" />
        <div className="result-icon-wrap"><img src={icon} alt="" /></div>
        <h1 className="result-title">{title}</h1>
        <p className="result-subtitle">{sub}</p>
        <div className="result-amount-box">
          <span className="result-amount-label">{amountLabel}</span>
          <span className="result-amount-value">
            <img src={IMG.coin} className="w-6 h-6 object-contain drop-shadow" alt="" />
            <span>{fmt(amount)}</span>
          </span>
        </div>
        {kind === 'win' && platformFee && platformFee > 0 ? (
          <div className="result-amount-fee">Platform fee: {fmt(platformFee)}</div>
        ) : null}
        <div className="result-btn-row">
          <button className="result-btn result-btn-secondary" onClick={onHome}>Home</button>
          <button className="result-btn result-btn-primary" onClick={onNewMatch}>New Match</button>
        </div>
      </div>
    </div>
  );
}

function RecoveryScreen({ kind, amount, platformFee, opponents, onAck }: {
  kind: 'win' | 'loss' | 'kick';
  amount: number;
  platformFee?: number;
  opponents?: Array<{ name?: string; color?: string; is_bot?: boolean }>;
  onAck: () => void;
}) {
  const icon = kind === 'win' ? IMG.victory : kind === 'kick' ? IMG.kicked : IMG.defeat;
  // [D] State class + title + subtitle + amount label all flow from the
  // outcome — mirrors PHP renderRecoveryModal (win-state/lose-state/
  // kicked-state + "YOU WON!" / "YOU LOST" / "DISCONNECTED").
  const stateClass =
    kind === 'win' ? 'win-state'
    : kind === 'kick' ? 'kicked-state'
    : 'lose-state';
  // Label + title split so "PREVIOUS GAME" renders as a small caption
  // above the big outcome word instead of jamming into one long line
  // that wraps awkwardly on narrow devices. Previously the single
  // string "PREVIOUS GAME · YOU WON!" overflowed the 360px card and
  // broke mid-word.
  const titleLabel = 'PREVIOUS GAME';
  const title =
    kind === 'win' ? 'YOU WON!'
    : kind === 'kick' ? 'DISCONNECTED'
    : 'YOU LOST';
  const amountLabel = kind === 'win' ? 'Credited' : 'Bet Forfeited';

  // Subtitle: "vs Rahul, Meera, Arjun" when opponents are present;
  // outcome-specific fallback otherwise. Uses past tense so it reads
  // as a report, not a live update.
  const oppNames = (opponents || []).map((o) => o?.name).filter(Boolean).join(', ');
  const sub = oppNames
    ? `Played vs ${oppNames}`
    : kind === 'win' ? 'Glory was yours'
    : kind === 'kick' ? 'You left while that match was in progress'
    : 'Better luck next round';

  return (
    <div id="recovery-screen" className="overlay-screen">
      <div className={`result-card ${stateClass}`}>
        <div className="result-rays" />
        <div className="result-icon-wrap"><img src={icon} alt="" /></div>
        <div className="result-title-label">{titleLabel}</div>
        <h1 className="result-title">{title}</h1>
        <p className="result-subtitle">{sub}</p>
        <div className="result-amount-box">
          <span className="result-amount-label">{amountLabel}</span>
          <span className="result-amount-value">
            <img src={IMG.coin} className="w-6 h-6 object-contain drop-shadow" alt="" />
            <span>{fmt(amount)}</span>
          </span>
        </div>
        {kind === 'win' && platformFee && platformFee > 0 ? (
          <div className="result-amount-fee">Platform fee: {fmt(platformFee)}</div>
        ) : null}
        <div className="result-btn-row">
          <button className="result-btn result-btn-primary" onClick={onAck}>Back to Lobby</button>
        </div>
      </div>
    </div>
  );
}

function QuitConfirm({ amount, onStay, onQuit }: {
  amount: number; onStay: () => void; onQuit: () => void;
}) {
  return (
    <div id="quit-confirm-modal" className="quit-backdrop">
      <div className="quit-card">
        <img src={IMG.warn} className="quit-icon" alt="" />
        <div className="quit-title">Forfeit the match?</div>
        <div className="quit-warning">
          Leaving now will instantly <b>end the game for you</b> and your bet will be <b>forfeited</b>.
          <br />
          You will lose <span className="quit-amount">{fmt(amount)}</span>
        </div>
        <div className="result-btn-row">
          <button className="result-btn result-btn-secondary" onClick={onStay}>Stay</button>
          <button className="result-btn result-btn-danger" onClick={onQuit}>Quit</button>
        </div>
      </div>
    </div>
  );
}

// [I] Matches PHP startCoinRain / stopCoinRain: every 350ms we spawn a
// fresh batch of 6 coins with random size/duration/delay; each coin
// removes itself ~4.5s after birth. Rain continues as long as the result
// card is mounted (PHP stops it on screen-dismiss; React unmount does
// the same because the interval clears in the cleanup).
function CoinRain() {
  const [coins, setCoins] = useState<Array<{
    key: number; left: number; size: number; duration: number; delay: number;
  }>>([]);

  useEffect(() => {
    let nextKey = 0;
    const spawn = () => {
      const batch = Array.from({ length: 6 }).map(() => ({
        key: nextKey++,
        left: Math.random() * 100,
        size: 10 + Math.random() * 10,
        duration: 2 + Math.random() * 2.2,
        delay: Math.random() * 0.5,
      }));
      setCoins((prev) => [...prev, ...batch]);
      // Each coin self-expires after ~4.5s so the DOM doesn't grow forever.
      const expireKeys = new Set(batch.map((b) => b.key));
      setTimeout(() => {
        setCoins((prev) => prev.filter((c) => !expireKeys.has(c.key)));
      }, 4500);
    };
    spawn();
    const interval = setInterval(spawn, 350);
    return () => clearInterval(interval);
  }, []);

  return (
    <div id="coin-rain" className="coin-rain">
      {coins.map((c) => (
        <span
          key={c.key}
          className="coin"
          style={{
            left: `${c.left}%`,
            width: c.size,
            height: c.size,
            animationDuration: `${c.duration}s`,
            animationDelay: `${c.delay}s`,
          }}
        />
      ))}
    </div>
  );
}
